import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import { formatRowsForExcel, applyExcelNumberFormat } from "../../utils/downloadUtils";
import {
  ThunderboltOutlined,
  LoadingOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  FileExcelOutlined,
  SaveOutlined,
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { App, Tabs, Steps, Button, Table, InputNumber, Divider, Slider, Select } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  fetchSimulations,
  fetchHybridBessHistory,
  fetchHybridSimulations,
  createHybridSimulation,
  fetchHybridDischargeProfile,
  saveHybridDischargeProfile,
  fetchHybridChargingProfile,
  saveHybridChargingProfile,
  fetchHybridGridChargingProfile,
  saveHybridGridChargingProfile,
  uploadSimulationGenerationProfile,
  runHybridSimulation,
  fetchHybridSimulationHourlyProfile,
  downloadHybridReport,
  downloadHybridDailyReport,
  deleteHybridSimulation,
  fetchContainers,
  fetchPCS,
  saveSimulation,
  fetchSimulationDetails,
} from "../../services/bessApi";

const TREND_FIELD_CONFIG = [
  { key: "solar_gen_33kv_mw", label: "Solar Gen at 33kV", color: "#f59e0b" },
  { key: "solar_to_bess_power", label: "Solar Power for BESS Charge", color: "#2563eb" },
  { key: "solar_power_at_poi", label: "Solar Power at POI", color: "#10b981" },
  { key: "grid_import_at_poi", label: "Grid Power at POI", color: "#ef4444" },
  { key: "bess_power_at_poi", label: "BESS Power at POI", color: "#8b5cf6" },
];

const MONTHS = [
  { key: "jan", label: "Jan" },
  { key: "feb", label: "Feb" },
  { key: "mar", label: "Mar" },
  { key: "apr", label: "Apr" },
  { key: "may", label: "May" },
  { key: "jun", label: "Jun" },
  { key: "jul", label: "Jul" },
  { key: "aug", label: "Aug" },
  { key: "sep", label: "Sep" },
  { key: "oct", label: "Oct" },
  { key: "nov", label: "Nov" },
  { key: "dec", label: "Dec" },
];

function PoiDeliveredChart({ data, targetVal }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const height = 260;
  const width = 500;
  const paddingLeft = 45;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data || data.length === 0) return null;

  const values = data.flatMap(d => [parseFloat(d.active_fleet?.poi_mwh) || 0, parseFloat(d.contracted_poi_mwh) || 0]);
  const minVal = 0;
  const maxVal = Math.max(...values, (targetVal || 0) * 1.1) || 120;

  const getX = (index) => {
    return paddingLeft + (index + 0.5) * (chartWidth / data.length);
  };

  const getY = (val) => {
    return paddingTop + chartHeight - ((val - minVal) / (maxVal - minVal)) * chartHeight;
  };

  const targetY = getY(targetVal || 0);
  const targetPath = `M ${paddingLeft} ${targetY} L ${width - paddingRight} ${targetY}`;

  const yTicks = [
    0,
    Math.round(maxVal * 0.25),
    Math.round(maxVal * 0.5),
    Math.round(maxVal * 0.75),
    Math.round(maxVal)
  ];

  const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

  return (
    <div className="relative bg-white p-2 h-full flex flex-col justify-between">
      <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
        <span>POI Energy vs Contract Target (MWh)</span>
        <div className="flex gap-3 text-[9px] font-semibold">
          <span className="flex items-center gap-1 text-red-500">
            <span className="w-2.5 h-0.5 bg-red-500 inline-block border-t border-red-500 border-dashed" />
            Target ({(targetVal || 0).toFixed(1)} MWh)
          </span>
          <span className="flex items-center gap-1 text-sky-500">
            <span className="w-3.5 h-2 bg-sky-500 rounded-sm inline-block" />
            POI Delivered
          </span>
        </div>
      </h5>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
        {yTicks.map((tick, i) => {
          const y = getY(tick);
          return (
            <g key={i} className="opacity-40">
              <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
              <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">{tick}</text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = getX(i);
          return (
            <text key={i} x={x} y={height - paddingBottom + 16} textAnchor="middle" className="text-[7.5px] font-bold fill-slate-400">{d.project_year}</text>
          );
        })}

        {/* Render bars for POI Delivered */}
        {data.map((d, i) => {
          const x = getX(i);
          const val = parseFloat(d.active_fleet?.poi_mwh || 0);
          if (val <= 0) return null;

          const barHeight = (val / maxVal) * chartHeight;
          const y = getY(val);

          return (
            <rect
              key={i}
              x={x - barWidth / 2}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={hoveredIndex === i ? "#0ea5e9" : "#0284c7"}
              rx="2"
              className="transition-all duration-200"
            />
          );
        })}

        {/* Target Line */}
        <path d={targetPath} fill="none" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="5 5" />

        {/* Hover zones */}
        {data.map((d, i) => {
          const x = getX(i);
          return (
            <g key={i}>
              <rect
                x={x - (chartWidth / data.length) / 2}
                y={paddingTop}
                width={chartWidth / data.length}
                height={chartHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {hoveredIndex === i && (
                <>
                  <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom} stroke="#94a3b8" strokeWidth="1" strokeDasharray="2 2" />
                </>
              )}
            </g>
          );
        })}
      </svg>

      {hoveredIndex !== null && data[hoveredIndex] && (
        <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
          <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">Project Year {data[hoveredIndex].project_year}</div>
          <div>POI: <span className="font-bold text-sky-400">{parseFloat(data[hoveredIndex].active_fleet?.poi_mwh || 0).toFixed(2)} MWh</span></div>
          <div>Target: <span className="font-bold text-red-400">{parseFloat(data[hoveredIndex].contracted_poi_mwh || 0).toFixed(2)} MWh</span></div>
          <div>Margin: <span className={`font-bold ${parseFloat(data[hoveredIndex].active_fleet?.poi_margin_mwh || 0) < 0 ? 'text-red-400' : 'text-emerald-400'}`}>{parseFloat(data[hoveredIndex].active_fleet?.poi_margin_mwh || 0).toFixed(2)} MWh</span></div>
        </div>
      )}
    </div>
  );
}

function AugmentationChart({ data }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const height = 260;
  const width = 500;
  const paddingLeft = 45;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 40;

  const chartWidth = width - paddingLeft - paddingRight;
  const chartHeight = height - paddingTop - paddingBottom;

  if (!data || data.length === 0) return null;

  const values = data.map(d => parseInt(d.augmented_fleet?.newly_purchased || 0, 10));
  const maxVal = Math.max(...values, 5) || 5;

  const getX = (index) => {
    return paddingLeft + (index + 0.5) * (chartWidth / data.length);
  };

  const getY = (val) => {
    return paddingTop + chartHeight - (val / maxVal) * chartHeight;
  };

  const yTicks = [
    0,
    Math.round(maxVal * 0.25),
    Math.round(maxVal * 0.5),
    Math.round(maxVal * 0.75),
    Math.round(maxVal)
  ];

  const barWidth = Math.max(4, (chartWidth / data.length) * 0.6);

  return (
    <div className="relative bg-white p-2 h-full flex flex-col justify-between">
      <h5 className="text-[12px] font-bold text-slate-700 mb-3 flex items-center justify-between">
        <span>Augmentation Schedule (Containers Purchased)</span>
        <div className="flex gap-2 text-[9px] font-semibold">
          <span className="flex items-center gap-1 text-amber-500">
            <span className="w-3.5 h-2 bg-amber-500 rounded-sm inline-block" />
            Containers Added
          </span>
        </div>
      </h5>

      <svg viewBox={`0 0 ${width} ${height}`} className="w-full flex-1 min-h-[180px]">
        {yTicks.map((tick, i) => {
          const y = getY(tick);
          return (
            <g key={i} className="opacity-40">
              <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
              <text x={paddingLeft - 8} y={y + 3} textAnchor="end" className="text-[8px] font-bold fill-slate-400">{tick}</text>
            </g>
          );
        })}

        {data.map((d, i) => {
          const x = getX(i);
          return (
            <text key={i} x={x} y={height - paddingBottom + 16} textAnchor="middle" className="text-[7.5px] font-bold fill-slate-400">{d.project_year}</text>
          );
        })}

        {/* Render bars for years with augmentation */}
        {data.map((d, i) => {
          const x = getX(i);
          const qty = parseInt(d.augmented_fleet?.newly_purchased || 0, 10);
          if (qty <= 0) return null;

          const barHeight = (qty / maxVal) * chartHeight;
          const y = getY(qty);

          return (
            <rect
              key={i}
              x={x - barWidth / 2}
              y={y}
              width={barWidth}
              height={barHeight}
              fill="#f59e0b"
              rx="2"
              className="transition-all duration-200"
            />
          );
        })}

        {/* Hover zones */}
        {data.map((d, i) => {
          const x = getX(i);
          return (
            <g key={i}>
              <rect
                x={x - (chartWidth / data.length) / 2}
                y={paddingTop}
                width={chartWidth / data.length}
                height={chartHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {hoveredIndex === i && (
                <>
                  <line x1={x} y1={paddingTop} x2={x} y2={height - paddingBottom} stroke="#94a3b8" strokeWidth="1" strokeDasharray="2 2" />
                </>
              )}
            </g>
          );
        })}
      </svg>

      {hoveredIndex !== null && data[hoveredIndex] && (
        <div className="absolute top-[65px] left-[55px] bg-slate-900/90 text-white rounded-lg p-2 text-[9px] space-y-0.5 shadow-lg pointer-events-none backdrop-blur-sm z-30 min-w-[120px]">
          <div className="font-bold text-slate-300 border-b border-slate-700 pb-0.5 mb-1">Project Year {data[hoveredIndex].project_year}</div>
          <div>Newly Purchased: <span className="font-bold text-amber-400">{parseInt(data[hoveredIndex].augmented_fleet?.newly_purchased || 0, 10)} Containers</span></div>
          <div>Total Active Ctrs: <span className="font-bold text-slate-300">{parseInt(data[hoveredIndex].active_fleet?.containers || 0, 10)} Containers</span></div>
        </div>
      )}
    </div>
  );
}

export default function HybridBessPage({ defaultTab }) {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation & History tab routing
  const isHistoryRoute = defaultTab === "history" || (location && location.pathname.endsWith("/history"));

  // Dropdowns & records state
  const [bessSimulations, setBessSimulations] = useState([]);
  const [hybridSimulations, setHybridSimulations] = useState([]);
  const [containerOptions, setContainerOptions] = useState([]);
  const [pcsOptions, setPcsOptions] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(isHistoryRoute ? "history" : "wizard");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (location && location.pathname.endsWith("/history")) {
      setActiveTab("history");
    } else if (location && location.pathname === "/hybrid-bess") {
      setActiveTab("wizard");
    }
  }, [location?.pathname]);

  // Form State (Step 1: Project Details)
  const [createdBessSizingSimId, setCreatedBessSizingSimId] = useState(null);
  const [step1Errors, setStep1Errors] = useState({});
  const [step1Touched, setStep1Touched] = useState({});

  // Sizing Preview State (Step 1)
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const validatePurchaseYears = (values) => {
    if (values.simulation_type !== "closed_loop") return null;
    if (!values.purchase_years || !Array.isArray(values.purchase_years) || values.purchase_years.length === 0) {
      return "At least one purchase year must be added";
    }
    const maxTenure = Number(values.simulation_tenure) || 50;
    for (const item of values.purchase_years) {
      if (typeof item === "object" && item !== null) {
        const yr = Number(item.year);
        const ctrs = Number(item.containers);
        if (item.year === "" || item.year === null || isNaN(yr) || yr < 1 || yr > maxTenure) {
          return `Purchase years must fall within simulation tenure (1 to ${maxTenure})`;
        }
        if (item.containers === "" || item.containers === null || isNaN(ctrs) || ctrs < 1) {
          return "Container count must be at least 1";
        }
      } else {
        const yr = Number(item);
        if (isNaN(yr) || yr < 1 || yr > maxTenure) {
          return `Purchase years must fall within simulation tenure (1 to ${maxTenure})`;
        }
      }
    }
    return null;
  };

  const handlePurchaseYearsChange = (updated) => {
    setProjectValues((prev) => ({ ...prev, purchase_years: updated }));
    if (step1Touched.purchase_years) {
      const err = validatePurchaseYears({ ...projectValues, purchase_years: updated });
      setStep1Errors((prev) => ({ ...prev, purchase_years: err }));
    }
  };

  const [projectValues, setProjectValues] = useState({
    name: "",
    container_batch: "",
    pcs: "",
    simulation_type: "closed_loop",
    operation_hour: 4,
    container_nos: "",
    pcs_nos: "",
    min_aug_period: "",
    min_aug_container: "",
    max_storage_capacity: "",
    start_year: "",
    simulation_tenure: "",
    energy_poi_mwh: "",
    purchase_years: [],

    // System Losses (Normal BESS)
    use_220kv_loss: true,
    transmission_line_220kv_loss: "",
    conductor_resistance_220kv_ohm: "",
    transmission_line_220kv_distance_km: "",
    use_transformer_220_loss: true,
    transformer_220kv_loss: "",

    use_400kv_loss: false,
    transmission_line_400kv_loss: "",
    conductor_resistance_400kv_ohm: "",
    transmission_line_400kv_distance_km: "",
    use_transformer_400_loss: false,
    transformer_400kv_loss: "",

    solar_aux_power: "",
    ht_ac_cable_loss: "",
    idt_loss: "",
    lt_ac_cable_loss: "",
    pcs_loss: "",
    lt_dc_cable_loss: "",
    bess_plant_unavailability: "",
  });

  const [bessSizingSimId, setBessSizingSimId] = useState("");
  const [simulationTenure, setSimulationTenure] = useState("");
  const [simulationType, setSimulationType] = useState("1"); // 1: Solar + BESS, 2: Solar + Wind + BESS

  const getHybridSimName = (s) => {
    if (s.name) return s.name.replace(/_hybrid$/i, "");
    const baseSim = bessSimulations.find((b) => b.id === parseInt(s.bess_sizing_simulation));
    const baseName = s.bess_sizing_simulation_name || baseSim?.name || (s.bess_sizing_simulation ? `Simulation #${s.bess_sizing_simulation}` : `Simulation #${s.id}`);
    return baseName.replace(/_hybrid$/i, "");
  };

  // Decimal parameters (string format for inputs)
  const [dt33kvPct, setDt33kvPct] = useState("");
  const [bessChargingEff, setBessChargingEff] = useState("");
  const [bessDischargingEff, setBessDischargingEff] = useState("");

  const [bessIdtCapacity, setBessIdtCapacity] = useState("");
  const [bessIdtNoLoad, setBessIdtNoLoad] = useState("");
  const [bessIdtLoad, setBessIdtLoad] = useState("");

  const [windIdtCapacity, setWindIdtCapacity] = useState("");
  const [windIdtNoLoad, setWindIdtNoLoad] = useState("");
  const [windIdtLoad, setWindIdtLoad] = useState("");

  const [trans220Capacity, setTrans220Capacity] = useState("");
  const [trans220NoLoad, setTrans220NoLoad] = useState("");
  const [trans220Load, setTrans220Load] = useState("");
  const [transLoss220kv, setTransLoss220kv] = useState("");
  const [useDirectTrans220Loss, setUseDirectTrans220Loss] = useState(false);
  const [has220Transformer, setHas220Transformer] = useState(true);

  const [trans400Capacity, setTrans400Capacity] = useState("");
  const [trans400NoLoad, setTrans400NoLoad] = useState("");
  const [trans400Load, setTrans400Load] = useState("");
  const [transLoss400kv, setTransLoss400kv] = useState("");
  const [useDirectTrans400Loss, setUseDirectTrans400Loss] = useState(false);
  const [has400Transformer, setHas400Transformer] = useState(true);

  const [conductorResistance220, setConductorResistance220] = useState("");
  const [lineDistance220, setLineDistance220] = useState("");
  const [conductorResistance400, setConductorResistance400] = useState("");
  const [lineDistance400, setLineDistance400] = useState("");
  const [has220Line, setHas220Line] = useState(true);
  const [transLineLoss220kv, setTransLineLoss220kv] = useState("");
  const [useDirectLineLoss220, setUseDirectLineLoss220] = useState(false);
  const [transLineLoss400kv, setTransLineLoss400kv] = useState("");
  const [useDirectLineLoss400, setUseDirectLineLoss400] = useState(false);
  const [has400Line, setHas400Line] = useState(true);

  const [bessUnavailability, setBessUnavailability] = useState("");
  const [gridUnavailability, setGridUnavailability] = useState("");

  const [htAcCableLoss, setHtAcCableLoss] = useState("");
  const [idtLoss, setIdtLoss] = useState("");
  const [ltAcCableLoss, setLtAcCableLoss] = useState("");
  const [ltDcCableLoss, setLtDcCableLoss] = useState("");
  const [pcsLoss, setPcsLoss] = useState("");

  const [evacuationCapacity, setEvacuationCapacity] = useState("");
  const [bessFeederCapacity, setBessFeederCapacity] = useState("");
  const [gridFeederCapacity, setGridFeederCapacity] = useState("");
  const [solarFeederCapacity, setSolarFeederCapacity] = useState("");

  const [minBessStoreEnergy, setMinBessStoreEnergy] = useState("");
  const [windTotalLossesPct, setWindTotalLossesPct] = useState("");
  const [minDispatchPower, setMinDispatchPower] = useState("");
  const [bessDegradation, setBessDegradation] = useState(1);
  const [adjustingFactor, setAdjustingFactor] = useState("1");

  // Yearly Min BESS Stored Energy State
  const [yearlyMinBessStoreEnergy, setYearlyMinBessStoreEnergy] = useState([]);

  // Generation & Dispatch Trends Field Selector State
  const [selectedTrendFields, setSelectedTrendFields] = useState([
    "solar_gen_33kv_mw",
    "solar_to_bess_power",
    "solar_power_at_poi",
    "grid_import_at_poi",
    "bess_power_at_poi",
  ]);
  const [savingYearlyMinBess, setSavingYearlyMinBess] = useState(false);
  const [bulkMinBessPct, setBulkMinBessPct] = useState("");

  const getTenureCount = () => {
    const parentBess = bessSimulations.find((s) => s.id === parseInt(bessSizingSimId));
    const rawTenure = simulationTenure || (parentBess ? parentBess.simulation_tenure : 25);
    return parseInt(rawTenure) || 25;
  };

  const buildYearlyMinBessList = (tenure, yearlyData, scalarVal) => {
    const defaultVal = scalarVal !== "" && scalarVal !== null && scalarVal !== undefined ? Number(scalarVal) : 100;

    const map = new Map();
    if (Array.isArray(yearlyData)) {
      yearlyData.forEach((item) => {
        if (item && item.year !== undefined) {
          map.set(Number(item.year), Number(item.pct));
        }
      });
    } else if (yearlyData && typeof yearlyData === "object" && yearlyData !== null) {
      Object.entries(yearlyData).forEach(([k, v]) => {
        if (typeof v === "object" && v !== null && v.pct !== undefined) {
          map.set(Number(k), Number(v.pct));
        } else {
          map.set(Number(k), Number(v));
        }
      });
    }

    const list = [];
    const totalYears = parseInt(tenure) || 25;
    for (let y = 1; y <= totalYears; y++) {
      const val = map.has(y) ? map.get(y) : defaultVal;
      list.push({ year: y, pct: val });
    }
    return list;
  };

  // Step-by-Step wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [createdSimId, setCreatedSimId] = useState(null);

  useEffect(() => {
    if (currentStep === 3) {
      const tenure = getTenureCount();
      if (yearlyMinBessStoreEnergy.length !== tenure) {
        setYearlyMinBessStoreEnergy((prev) =>
          buildYearlyMinBessList(tenure, prev.length > 0 ? prev : null, minBessStoreEnergy)
        );
      }
    }
  }, [currentStep, bessSizingSimId, simulationTenure]);

  const buildDefault24HourMatrix = () => {
    return Array.from({ length: 24 }, (_, i) => {
      const hour = i + 1;
      const row = { hour };
      MONTHS.forEach((m) => {
        row[m.key] = false;
      });
      return row;
    });
  };

  // Step 3: Discharge Profile state
  const [dischargeProfile, setDischargeProfile] = useState(buildDefault24HourMatrix);
  const [loadingDischarge, setLoadingDischarge] = useState(false);
  const [savingDischarge, setSavingDischarge] = useState(false);

  // Step 4: Charging Profile state
  const [chargingProfile, setChargingProfile] = useState(buildDefault24HourMatrix);
  const [loadingCharging, setLoadingCharging] = useState(false);
  const [savingCharging, setSavingCharging] = useState(false);

  // Step 5: Grid Charging Profile state
  const [gridChargingProfile, setGridChargingProfile] = useState(buildDefault24HourMatrix);
  const [loadingGridCharging, setLoadingGridCharging] = useState(false);
  const [savingGridCharging, setSavingGridCharging] = useState(false);
  const [downloadingSims, setDownloadingSims] = useState({});

  // Step 3: Profile Upload & Run state
  const [uploadFile, setUploadFile] = useState(null);
  const [solarDegradation, setSolarDegradation] = useState("0.4");
  const [uploadDataType, setUploadDataType] = useState("single");
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [runningSim, setRunningSim] = useState(false);
  const [simulationResults, setSimulationResults] = useState(null);
  const [loadedFromHistory, setLoadedFromHistory] = useState(false);
  const [profileFilename, setProfileFilename] = useState("");

  // Charting Detail state
  const [selectedDetailsYear, setSelectedDetailsYear] = useState(null);
  const [hourlyProfileData, setHourlyProfileData] = useState([]);
  const [loadingHourlyData, setLoadingHourlyData] = useState(false);
  const [selectedChartDay, setSelectedChartDay] = useState(1);

  // Excel Download state
  const [downloadingExcelYear, setDownloadingExcelYear] = useState(null);
  const [selectedExportYear, setSelectedExportYear] = useState("all");

  const handleDownloadYearExcel = async (yearTarget = "all") => {
    const simId = createdSimId;
    if (!simId) {
      message.error("No active simulation found.");
      return;
    }
    setDownloadingExcelYear(yearTarget);
    message.loading({
      content: `Downloading Excel report for ${yearTarget === "all" ? "all years" : `Year ${yearTarget}`}...`,
      key: "download-excel",
    });
    try {
      const dailyData = await downloadHybridDailyReport(simId, yearTarget);
      const dailyProfile = dailyData.hourly_profile || [];
      if (!dailyProfile || dailyProfile.length === 0) {
        message.warning({ content: "No daily profile data available to download.", key: "download-excel" });
        return;
      }

      const wb = XLSX.utils.book_new();

      const yearMap = new Map();
      dailyProfile.forEach((row) => {
        const yr = row.year || 1;
        if (!yearMap.has(yr)) {
          yearMap.set(yr, []);
        }
        yearMap.get(yr).push(row);
      });

      Array.from(yearMap.keys()).sort((a, b) => a - b).forEach((yearNum) => {
        const rowsForYear = yearMap.get(yearNum);
        const excelRows = rowsForYear.map((row) => ({
          "Year": row.year,
          "Month": row.month_num ?? row.month,
          "Day": row.day,
          "Hour": row.hour !== undefined && row.hour !== null ? Number(row.hour) : (row.block ? (((row.block - 1) % 24) + 1) : ""),
          "Ambient Temp C": row.ambient_temp_c,
          "Solar Gen 33Kv Mw": row.solar_gen_33kv_mw,
          "Solar Aux Power": row.solar_aux_power,
          "Solar Power At 33Kv": row.solar_power_at_33kv,
          "Solar Power After DT": row.solar_power_after_dt,
          "Battery State": row.battery_state,
          "Total Container No": row.total_container_no,
          "Aux Standby Mw": row.aux_standby_mw ?? row.bess_aux_standby_mw,
          "Aux Operation Mw": row.aux_operation_mw ?? row.bess_aux_operation_mw,
          "Solar Power After Aux": row.solar_power_after_aux ?? row.solar_power_after_bess_aux,
          "Solar Set Point": row.solar_set_point,
          "Curtail Power For Bess Charging": row.solar_to_bess_power ?? row.power_for_bess_charging_at_33kv_bus,
          "Export Solar Power": row.export_solar_power,
          "Bess Capacity": row.bess_capacity,
          "BESS Charge After Losses": row.bess_charge_after_losses,
          "BESS Charge From Solar After Losses": row.bess_solar_after_loss ?? row.bess_charge_from_solar_power,
          "Grid Import For Bess Charging": row.grid_import_for_bess_charging,
          "Grid Import At 33Kv Bus For BESS Charge": row.grid_import_at_33kv_bus,
          "Grid Import At 220Kv Bus For BESS Charge": row.grid_import_at_220kv_bus,
          "Grid Import At 400Kv Bus For BESS Charge": row.grid_import_at_400kv_bus,
          "Grid Import At POI For BESS Charge": row.grid_import_at_poi,
          "Total BESS energy": row.total_bess,
          "Bess Power": row.bess_power,
          "Bess Power After Aux": row.bess_power_after_aux,
          "Bess Power At 33Kv Bus": row.bess_power_at_33kv_bus,
          "Bess Power At 220Kv Bus": row.bess_power_at_220kv_bus,
          "Bess Power At 400Kv Bus": row.bess_power_at_400kv_bus,
          "Bess Power At POI": row.bess_power_at_poi,
          "Bess Power After Unavailability": row.bess_power_after_unavailability,
          "Curtailed BESS Power At 33Kv Bus": row.export_solar_power,
          "Solar Power At 220Kv Bus": row.solar_power_at_220kv_bus,
          "Solar Power At 400Kv Bus": row.solar_power_at_400kv_bus,
          "Solar Power At POI": row.solar_power_at_poi,
          "Solar Power After Unavailability": row.solar_power_after_unavailability,
        }));

        const ws = XLSX.utils.json_to_sheet(formatRowsForExcel(excelRows));
        applyExcelNumberFormat(ws);
        XLSX.utils.book_append_sheet(wb, ws, `Year ${yearNum}`);
      });

      const fileName = yearTarget === "all"
        ? `Hybrid_BESS_Simulation_${simId}_Daily_Outputs.xlsx`
        : `Hybrid_BESS_Simulation_${simId}_Daily_Year_${yearTarget}.xlsx`;
      XLSX.writeFile(wb, fileName);
      message.success({ content: "Excel report downloaded successfully!", key: "download-excel" });
    } catch (err) {
      console.error(err);
      message.error({ content: "Failed to download Excel report: " + err.message, key: "download-excel" });
    } finally {
      setDownloadingExcelYear(null);
    }
  };

  // Load equipment options and BESS simulations for dropdowns
  const loadDropdowns = async () => {
    try {
      setLoadingDropdowns(true);
      const [containers, pcsList, sims, hybridSims] = await Promise.all([
        fetchContainers(),
        fetchPCS(),
        fetchSimulations(),
        fetchHybridBessHistory(),
      ]);
      setContainerOptions(containers || []);
      setPcsOptions(pcsList || []);
      setBessSimulations(sims || []);
      setHybridSimulations(hybridSims || []);

      if (containers && containers.length > 0) {
        setProjectValues((prev) => ({
          ...prev,
          container_batch: prev.container_batch || containers[0].id,
        }));
      }
      if (pcsList && pcsList.length > 0) {
        setProjectValues((prev) => ({
          ...prev,
          pcs: prev.pcs || pcsList[0].id,
        }));
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to load equipment options: " + err.message);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Load existing Hybrid simulations
  const loadHybridSimulations = async () => {
    try {
      setLoadingList(true);
      const data = await fetchHybridBessHistory();
      setHybridSimulations(data || []);
    } catch (err) {
      message.error("Failed to load Hybrid BESS Simulations: " + err.message);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    let active = true;
    setTimeout(() => {
      if (active) {
        loadDropdowns();
        loadHybridSimulations();
      }
    }, 0);
    return () => {
      active = false;
    };
  }, []);

  const handleDownloadTemplate = () => {
    const tenure = parseInt(simulationTenure || projectValues.simulation_tenure || 1);
    const yearsToGenerate = uploadDataType === "single" ? 1 : tenure;

    // Create sample data for the first block
    const sampleRow = {
      block: 1,
      ambient_temp_c: 22.8
    };

    for (let i = 1; i <= yearsToGenerate; i++) {
      sampleRow[`year_${i}`] = 10.036118; // Default value for solar generation
    }

    const sampleRows = [sampleRow];
    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "data");
    XLSX.writeFile(workbook, "generation_profile_template.xlsx");
  };

  // Fetch discharge profile as soon as createdSimId is resolved and we enter Step 3
  useEffect(() => {
    if (currentStep === 3 && createdSimId) {
      const loadProfile = async () => {
        try {
          setLoadingDischarge(true);
          const data = await fetchHybridDischargeProfile(createdSimId);
          setDischargeProfile(data && data.length > 0 ? data : buildDefault24HourMatrix());
        } catch (err) {
          message.error("Failed to load discharge profile: " + err.message);
        } finally {
          setLoadingDischarge(false);
        }
      };
      loadProfile();
    }
  }, [currentStep, createdSimId]);

  // Fetch charging profile as soon as createdSimId is resolved and we enter Step 4
  useEffect(() => {
    if (currentStep === 4 && createdSimId) {
      const loadProfile = async () => {
        try {
          setLoadingCharging(true);
          const data = await fetchHybridChargingProfile(createdSimId);
          setChargingProfile(data && data.length > 0 ? data : buildDefault24HourMatrix());
        } catch (err) {
          message.error("Failed to load charging profile: " + err.message);
        } finally {
          setLoadingCharging(false);
        }
      };
      loadProfile();
    }
  }, [currentStep, createdSimId]);

  // Fetch grid charging profile as soon as createdSimId is resolved and we enter Step 5
  useEffect(() => {
    if (currentStep === 5 && createdSimId) {
      const loadProfile = async () => {
        try {
          setLoadingGridCharging(true);
          const data = await fetchHybridGridChargingProfile(createdSimId);
          setGridChargingProfile(data && data.length > 0 ? data : buildDefault24HourMatrix());
        } catch (err) {
          message.error("Failed to load grid charging profile: " + err.message);
        } finally {
          setLoadingGridCharging(false);
        }
      };
      loadProfile();
    }
  }, [currentStep, createdSimId]);

  // Handle dropdown change on Bess Sizing Simulation to auto-fill Simulation Tenure and BESS losses
  const handleBessSizingSimChange = (simId) => {
    setBessSizingSimId(simId);
    if (!simId) {
      setSimulationTenure("");
      setHtAcCableLoss("");
      setIdtLoss("");
      setLtAcCableLoss("");
      setLtDcCableLoss("");
      setPcsLoss("");
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setBessUnavailability("");
      return;
    }
    const matched = bessSimulations.find((s) => s.id === parseInt(simId));
    if (matched) {
      setSimulationTenure(matched.simulation_tenure ? matched.simulation_tenure.toString() : "");
      setHtAcCableLoss(matched.ht_ac_cable_loss != null ? matched.ht_ac_cable_loss.toString() : "");
      setIdtLoss(matched.idt_loss != null ? matched.idt_loss.toString() : "");
      setLtAcCableLoss(matched.lt_ac_cable_loss != null ? matched.lt_ac_cable_loss.toString() : "");
      setLtDcCableLoss(matched.lt_dc_cable_loss != null ? matched.lt_dc_cable_loss.toString() : "");
      setPcsLoss(matched.pcs_loss != null ? matched.pcs_loss.toString() : "");
      const rawUnavail = matched.bess_plant_unavailability ?? matched.bess_unavailability;
      setBessUnavailability(rawUnavail != null && rawUnavail !== "" ? parseFloat(rawUnavail).toString() : "");

      // Transformer 220kV selection from Normal BESS
      const isUse220Trans = matched.use_transformer_220_loss !== undefined && matched.use_transformer_220_loss !== null
        ? Boolean(matched.use_transformer_220_loss)
        : ((matched.transformer_220kv_loss ?? matched.transformer_220_loss) != null && (matched.transformer_220kv_loss ?? matched.transformer_220_loss) !== "" && Number(matched.transformer_220kv_loss ?? matched.transformer_220_loss) > 0);
      setHas220Transformer(isUse220Trans);
      setUseDirectTrans220Loss(isUse220Trans);
      const val220Trans = matched.transformer_220kv_loss ?? matched.transformer_220_loss;
      setTransLoss220kv(isUse220Trans && val220Trans != null ? val220Trans.toString() : "");

      // Transformer 400kV selection from Normal BESS
      const isUse400Trans = matched.use_transformer_400_loss !== undefined && matched.use_transformer_400_loss !== null
        ? Boolean(matched.use_transformer_400_loss)
        : ((matched.transformer_400kv_loss ?? matched.transformer_400_220_loss ?? matched.transformer_400_loss) != null && (matched.transformer_400kv_loss ?? matched.transformer_400_220_loss ?? matched.transformer_400_loss) !== "" && Number(matched.transformer_400kv_loss ?? matched.transformer_400_220_loss ?? matched.transformer_400_loss) > 0);
      setHas400Transformer(isUse400Trans);
      setUseDirectTrans400Loss(isUse400Trans);
      const val400Trans = matched.transformer_400kv_loss ?? matched.transformer_400_220_loss ?? matched.transformer_400_loss;
      setTransLoss400kv(isUse400Trans && val400Trans != null ? val400Trans.toString() : "");

      // Transmission Line 220kV selection from Normal BESS
      const isUse220Line = matched.use_220kv_loss !== undefined && matched.use_220kv_loss !== null
        ? Boolean(matched.use_220kv_loss)
        : ((matched.transmission_line_220kv_loss ?? matched.transmission_line_loss) != null && (matched.transmission_line_220kv_loss ?? matched.transmission_line_loss) !== "" && Number(matched.transmission_line_220kv_loss ?? matched.transmission_line_loss) > 0);
      setHas220Line(isUse220Line);
      setUseDirectLineLoss220(isUse220Line);
      const val220Line = matched.transmission_line_220kv_loss ?? matched.transmission_line_loss;
      setTransLineLoss220kv(isUse220Line && val220Line != null ? val220Line.toString() : "");
      if (isUse220Line) {
        setConductorResistance220("");
        setLineDistance220("");
      } else {
        const res220 = matched.conductor_resistance_220kv_ohm ?? (matched.transmission_line_220kv_loss == null ? matched.conductor_resistance_ohm : null);
        setConductorResistance220(res220 != null ? res220.toString() : "");
        const dist220 = matched.transmission_line_220kv_distance_km ?? (matched.transmission_line_220kv_loss == null ? matched.transmission_line_distance_km : null);
        setLineDistance220(dist220 != null ? dist220.toString() : "");
      }

      // Transmission Line 400kV selection from Normal BESS
      const isUse400Line = matched.use_400kv_loss !== undefined && matched.use_400kv_loss !== null
        ? Boolean(matched.use_400kv_loss)
        : (matched.transmission_line_400kv_loss != null && matched.transmission_line_400kv_loss !== "" && Number(matched.transmission_line_400kv_loss) > 0);
      setHas400Line(isUse400Line);
      setUseDirectLineLoss400(isUse400Line);
      const val400Line = matched.transmission_line_400kv_loss;
      setTransLineLoss400kv(isUse400Line && val400Line != null ? val400Line.toString() : "");
      if (isUse400Line) {
        setConductorResistance400("");
        setLineDistance400("");
      } else {
        const res400 = matched.conductor_resistance_400kv_ohm;
        setConductorResistance400(res400 != null ? res400.toString() : "");
        const dist400 = matched.transmission_line_400kv_distance_km;
        setLineDistance400(dist400 != null ? dist400.toString() : "");
      }

      // Update Step 1 projectValues from selected BESS simulation
      setProjectValues((prev) => ({
        ...prev,
        container_batch: matched.container_batch || prev.container_batch,
        pcs: matched.pcs || prev.pcs,
        simulation_type: matched.simulation_type || prev.simulation_type,
        operation_hour: matched.operation_hour ?? prev.operation_hour,
        container_nos: matched.container_nos ?? prev.container_nos,
        pcs_nos: matched.pcs_nos ?? prev.pcs_nos,
        start_year: matched.start_year ?? prev.start_year,
        simulation_tenure: matched.simulation_tenure ?? prev.simulation_tenure,
        energy_poi_mwh: matched.energy_poi_mwh ?? prev.energy_poi_mwh,
        purchase_years: matched.purchase_years || prev.purchase_years,

        // System losses
        use_220kv_loss: isUse220Line,
        transmission_line_220kv_loss: val220Line != null ? val220Line.toString() : prev.transmission_line_220kv_loss,
        use_transformer_220_loss: isUse220Trans,
        transformer_220kv_loss: val220Trans != null ? val220Trans.toString() : prev.transformer_220kv_loss,
        use_400kv_loss: isUse400Line,
        transmission_line_400kv_loss: val400Line != null ? val400Line.toString() : prev.transmission_line_400kv_loss,
        use_transformer_400_loss: isUse400Trans,
        transformer_400kv_loss: val400Trans != null ? val400Trans.toString() : prev.transformer_400kv_loss,
        solar_aux_power: matched.solar_aux_power ?? prev.solar_aux_power,
        ht_ac_cable_loss: matched.ht_ac_cable_loss ?? prev.ht_ac_cable_loss,
        idt_loss: matched.idt_loss ?? prev.idt_loss,
        lt_ac_cable_loss: matched.lt_ac_cable_loss ?? prev.lt_ac_cable_loss,
        pcs_loss: matched.pcs_loss ?? prev.pcs_loss,
        lt_dc_cable_loss: matched.lt_dc_cable_loss ?? prev.lt_dc_cable_loss,
        bess_plant_unavailability: rawUnavail != null && rawUnavail !== "" ? parseFloat(rawUnavail).toString() : prev.bess_plant_unavailability,
        tl_volt_stage_1: matched.tl_volt_stage_1 ?? prev.tl_volt_stage_1,
        tl_volt_stage_2: matched.tl_volt_stage_2 ?? prev.tl_volt_stage_2,
      }));
    } else {
      setSimulationTenure("");
      setHtAcCableLoss("");
      setIdtLoss("");
      setLtAcCableLoss("");
      setLtDcCableLoss("");
      setPcsLoss("");
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
      setHas220Transformer(true);
      setHas400Transformer(true);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setHas220Line(true);
      setHas400Line(true);
      setConductorResistance220("");
      setLineDistance220("");
      setConductorResistance400("");
      setLineDistance400("");
      setBessUnavailability("");
    }
  };

  const handleLoadSimulation = async (sim) => {
    setCreatedSimId(sim.id);
    const parentBessId = (sim.bess_sizing_simulation || "").toString();
    setBessSizingSimId(parentBessId);
    if (sim.bess_sizing_simulation) {
      setCreatedBessSizingSimId(sim.bess_sizing_simulation);
    }
    setProfileFilename(sim.profile_filename || "");
    setSimulationTenure((sim.simulation_tenure || "").toString());
    setSimulationType((sim.simulation_type || "1").toString());
    setDt33kvPct((sim.dt_33kv_pct || "").toString());
    setDt33kvPct((sim.dt_33kv_pct || "").toString());
    setBessChargingEff((sim.bess_charging_efficiency_pct || "").toString());
    setBessDischargingEff((sim.bess_discharging_efficiency_pct || "").toString());

    setBessIdtCapacity((sim.bess_idt_installed_capacity_mva || "").toString());
    setBessIdtNoLoad((sim.bess_idt_noload_losses_mva || "").toString());
    setBessIdtLoad((sim.bess_idt_load_losses_mva || "").toString());
    setWindIdtCapacity((sim.wind_idt_installed_capacity_mva || "").toString());
    setWindIdtNoLoad((sim.wind_idt_noload_losses_mva || "").toString());
    setWindIdtLoad((sim.wind_idt_load_losses_mva || "").toString());

    setTrans220Capacity((sim.transformer_220kv_capacity_mva || "").toString());
    setTrans220NoLoad((sim.transformer_220kv_noload_losses_mva || "").toString());
    setTrans220Load((sim.transformer_220kv_load_losses_mva || "").toString());

    setTrans400Capacity((sim.transformer_400kv_capacity_mva || "").toString());
    setTrans400NoLoad((sim.transformer_400kv_noload_losses_mva || "").toString());
    setTrans400Load((sim.transformer_400kv_load_losses_mva || "").toString());

    const parentBess = bessSimulations.find((s) => s.id === parseInt(parentBessId));

    // Populate Step 1 (Project Details) state:
    const loadedName = (parentBess?.name || sim.name || (sim.bess_sizing_simulation ? `Simulation #${sim.bess_sizing_simulation}` : `Simulation #${sim.id}`)).replace(/_hybrid$/i, "");
    setProjectValues({
      name: loadedName,
      container_batch: sim.container_batch || parentBess?.container_batch || (containerOptions[0]?.id || ""),
      pcs: sim.pcs || parentBess?.pcs || (pcsOptions[0]?.id || ""),
      simulation_type: parentBess?.simulation_type || sim.bess_simulation_type || "closed_loop",
      operation_hour: sim.operation_hour ?? parentBess?.operation_hour ?? 4,
      container_nos: sim.container_nos ?? parentBess?.container_nos ?? "",
      pcs_nos: sim.pcs_nos ?? parentBess?.pcs_nos ?? "",
      min_aug_period: sim.min_aug_period ?? parentBess?.min_aug_period ?? "",
      min_aug_container: sim.min_aug_container ?? parentBess?.min_aug_container ?? "",
      max_storage_capacity: sim.max_storage_capacity ?? parentBess?.max_storage_capacity ?? "",
      start_year: sim.start_year ?? parentBess?.start_year ?? "",
      simulation_tenure: sim.simulation_tenure ?? parentBess?.simulation_tenure ?? "",
      energy_poi_mwh: sim.energy_poi_mwh ?? parentBess?.energy_poi_mwh ?? "",
      purchase_years: sim.purchase_years || parentBess?.purchase_years || [],

      // System Losses (Step 1)
      use_220kv_loss: sim.use_220kv_loss ?? parentBess?.use_220kv_loss ?? (sim.transmission_line_220kv_loss != null && sim.transmission_line_220kv_loss !== ""),
      transmission_line_220kv_loss: sim.transmission_line_220kv_loss ?? parentBess?.transmission_line_220kv_loss ?? "",
      conductor_resistance_220kv_ohm: sim.conductor_resistance_220kv_ohm ?? parentBess?.conductor_resistance_220kv_ohm ?? "",
      transmission_line_220kv_distance_km: sim.transmission_line_220kv_distance_km ?? parentBess?.transmission_line_220kv_distance_km ?? "",

      use_transformer_220_loss: sim.use_transformer_220_loss ?? parentBess?.use_transformer_220_loss ?? (sim.transformer_220kv_loss != null && sim.transformer_220kv_loss !== ""),
      transformer_220kv_loss: sim.transformer_220kv_loss ?? parentBess?.transformer_220kv_loss ?? "",

      use_400kv_loss: sim.use_400kv_loss ?? parentBess?.use_400kv_loss ?? (sim.transmission_line_400kv_loss != null && sim.transmission_line_400kv_loss !== ""),
      transmission_line_400kv_loss: sim.transmission_line_400kv_loss ?? parentBess?.transmission_line_400kv_loss ?? "",
      conductor_resistance_400kv_ohm: sim.conductor_resistance_400kv_ohm ?? parentBess?.conductor_resistance_400kv_ohm ?? "",
      transmission_line_400kv_distance_km: sim.transmission_line_400kv_distance_km ?? parentBess?.transmission_line_400kv_distance_km ?? "",

      use_transformer_400_loss: sim.use_transformer_400_loss ?? parentBess?.use_transformer_400_loss ?? (sim.transformer_400kv_loss != null && sim.transformer_400kv_loss !== ""),
      transformer_400kv_loss: sim.transformer_400kv_loss ?? parentBess?.transformer_400kv_loss ?? "",

      solar_aux_power: sim.solar_aux_power ?? parentBess?.solar_aux_power ?? "",
      ht_ac_cable_loss: sim.ht_ac_cable_loss ?? parentBess?.ht_ac_cable_loss ?? "",
      idt_loss: sim.idt_loss ?? parentBess?.idt_loss ?? "",
      lt_ac_cable_loss: sim.lt_ac_cable_loss ?? parentBess?.lt_ac_cable_loss ?? "",
      pcs_loss: sim.pcs_loss ?? parentBess?.pcs_loss ?? "",
      lt_dc_cable_loss: sim.lt_dc_cable_loss ?? parentBess?.lt_dc_cable_loss ?? "",
      bess_plant_unavailability: sim.bess_plant_unavailability ?? sim.bess_unavailability ?? parentBess?.bess_plant_unavailability ?? parentBess?.bess_unavailability ?? "",
      tl_volt_stage_1: sim.tl_volt_stage_1 ?? parentBess?.tl_volt_stage_1 ?? "",
      tl_volt_stage_2: sim.tl_volt_stage_2 ?? parentBess?.tl_volt_stage_2 ?? "",
    });

    // 220kV Transformer: Prioritize loaded hybrid simulation's configuration
    if (sim.transformer_220kv_loss != null && sim.transformer_220kv_loss !== "") {
      setHas220Transformer(true);
      setUseDirectTrans220Loss(true);
      setTransLoss220kv(sim.transformer_220kv_loss.toString());
    } else if (sim.transformer_220kv_capacity_mva != null && parseFloat(sim.transformer_220kv_capacity_mva) > 0) {
      setHas220Transformer(true);
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
    } else if (sim.transformer_220kv_capacity_mva === null && sim.transformer_220kv_loss === null) {
      setHas220Transformer(false);
      setUseDirectTrans220Loss(false);
      setTransLoss220kv("");
    } else if (parentBess) {
      const isUse220Trans = parentBess.use_transformer_220_loss !== undefined && parentBess.use_transformer_220_loss !== null
        ? Boolean(parentBess.use_transformer_220_loss)
        : ((parentBess.transformer_220kv_loss ?? parentBess.transformer_220_loss) != null && Number(parentBess.transformer_220kv_loss ?? parentBess.transformer_220_loss) > 0);
      setHas220Transformer(isUse220Trans);
      setUseDirectTrans220Loss(isUse220Trans);
      setTransLoss220kv(isUse220Trans ? (parentBess.transformer_220kv_loss ?? parentBess.transformer_220_loss ?? "").toString() : "");
    }

    // 400kV Transformer: Prioritize loaded hybrid simulation's configuration
    if (sim.transformer_400kv_loss != null && sim.transformer_400kv_loss !== "") {
      setHas400Transformer(true);
      setUseDirectTrans400Loss(true);
      setTransLoss400kv(sim.transformer_400kv_loss.toString());
    } else if (sim.transformer_400kv_capacity_mva != null && parseFloat(sim.transformer_400kv_capacity_mva) > 0) {
      setHas400Transformer(true);
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
    } else if (sim.transformer_400kv_capacity_mva === null && sim.transformer_400kv_loss === null) {
      setHas400Transformer(false);
      setUseDirectTrans400Loss(false);
      setTransLoss400kv("");
    } else if (parentBess) {
      const isUse400Trans = parentBess.use_transformer_400_loss !== undefined && parentBess.use_transformer_400_loss !== null
        ? Boolean(parentBess.use_transformer_400_loss)
        : ((parentBess.transformer_400kv_loss ?? parentBess.transformer_400_220_loss ?? parentBess.transformer_400_loss) != null && Number(parentBess.transformer_400kv_loss ?? parentBess.transformer_400_220_loss ?? parentBess.transformer_400_loss) > 0);
      setHas400Transformer(isUse400Trans);
      setUseDirectTrans400Loss(isUse400Trans);
      setTransLoss400kv(isUse400Trans ? (parentBess.transformer_400kv_loss ?? parentBess.transformer_400_220_loss ?? parentBess.transformer_400_loss ?? "").toString() : "");
    }

    // 220kV Transmission Line: Prioritize loaded hybrid simulation's configuration
    if (sim.transmission_line_220kv_loss != null && sim.transmission_line_220kv_loss !== "") {
      setHas220Line(true);
      setUseDirectLineLoss220(true);
      setTransLineLoss220kv(sim.transmission_line_220kv_loss.toString());
      setConductorResistance220("");
      setLineDistance220("");
    } else if ((sim.conductor_resistance_220kv_ohm != null && sim.conductor_resistance_220kv_ohm !== "") || (sim.transmission_line_220kv_distance_km != null && sim.transmission_line_220kv_distance_km !== "")) {
      setHas220Line(true);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220(sim.conductor_resistance_220kv_ohm.toString());
      setLineDistance220(sim.transmission_line_220kv_distance_km.toString());
    } else if (parentBess && ((parentBess.transmission_line_220kv_loss ?? parentBess.transmission_line_loss) != null && Number(parentBess.transmission_line_220kv_loss ?? parentBess.transmission_line_loss) > 0)) {
      setHas220Line(true);
      setUseDirectLineLoss220(true);
      setTransLineLoss220kv((parentBess.transmission_line_220kv_loss ?? parentBess.transmission_line_loss).toString());
      setConductorResistance220("");
      setLineDistance220("");
    } else if ((sim.conductor_resistance_ohm != null && sim.conductor_resistance_ohm !== "") || (sim.transmission_line_distance_km != null && sim.transmission_line_distance_km !== "")) {
      setHas220Line(true);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220(sim.conductor_resistance_ohm != null ? sim.conductor_resistance_ohm.toString() : "");
      setLineDistance220(sim.transmission_line_distance_km != null ? sim.transmission_line_distance_km.toString() : "");
    } else {
      setHas220Line(false);
      setUseDirectLineLoss220(false);
      setTransLineLoss220kv("");
      setConductorResistance220("");
      setLineDistance220("");
    }

    // 400kV Transmission Line: Prioritize loaded hybrid simulation's configuration
    if (sim.transmission_line_400kv_loss != null && sim.transmission_line_400kv_loss !== "") {
      setHas400Line(true);
      setUseDirectLineLoss400(true);
      setTransLineLoss400kv(sim.transmission_line_400kv_loss.toString());
      setConductorResistance400("");
      setLineDistance400("");
    } else if ((sim.conductor_resistance_400kv_ohm != null && sim.conductor_resistance_400kv_ohm !== "") || (sim.transmission_line_400kv_distance_km != null && sim.transmission_line_400kv_distance_km !== "")) {
      setHas400Line(true);
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setConductorResistance400(sim.conductor_resistance_400kv_ohm != null ? sim.conductor_resistance_400kv_ohm.toString() : "");
      setLineDistance400(sim.transmission_line_400kv_distance_km != null ? sim.transmission_line_400kv_distance_km.toString() : "");
    } else if (parentBess && (parentBess.transmission_line_400kv_loss != null && Number(parentBess.transmission_line_400kv_loss) > 0)) {
      setHas400Line(true);
      setUseDirectLineLoss400(true);
      setTransLineLoss400kv(parentBess.transmission_line_400kv_loss.toString());
      setConductorResistance400("");
      setLineDistance400("");
    } else {
      setHas400Line(false);
      setUseDirectLineLoss400(false);
      setTransLineLoss400kv("");
      setConductorResistance400("");
      setLineDistance400("");
    }

    const loadUnavail = sim.bess_unavailability != null && sim.bess_unavailability !== ""
      ? sim.bess_unavailability
      : (parentBess ? (parentBess.bess_plant_unavailability ?? parentBess.bess_unavailability ?? "") : "");
    setBessUnavailability(loadUnavail != null && loadUnavail !== "" ? parseFloat(loadUnavail).toString() : "");
    setGridUnavailability((sim.grid_unavailability ?? sim.plant_unavailability ?? "").toString());

    setHtAcCableLoss(sim.ht_ac_cable_loss != null ? sim.ht_ac_cable_loss.toString() : (parentBess?.ht_ac_cable_loss != null ? parentBess.ht_ac_cable_loss.toString() : ""));
    setIdtLoss(sim.idt_loss != null ? sim.idt_loss.toString() : (parentBess?.idt_loss != null ? parentBess.idt_loss.toString() : ""));
    setLtAcCableLoss(sim.lt_ac_cable_loss != null ? sim.lt_ac_cable_loss.toString() : (parentBess?.lt_ac_cable_loss != null ? parentBess.lt_ac_cable_loss.toString() : ""));
    setLtDcCableLoss(sim.lt_dc_cable_loss != null ? sim.lt_dc_cable_loss.toString() : (parentBess?.lt_dc_cable_loss != null ? parentBess.lt_dc_cable_loss.toString() : ""));
    setPcsLoss(sim.pcs_loss != null ? sim.pcs_loss.toString() : (parentBess?.pcs_loss != null ? parentBess.pcs_loss.toString() : ""));
    setEvacuationCapacity((sim.evacuation_capacity_mva || "").toString());
    setBessFeederCapacity((sim.bess_feeder_capacity_mva || "").toString());
    setGridFeederCapacity((sim.grid_feeder_capacity_mva || "").toString());
    const scalarVal = (sim.min_bess_store_energy || sim.minBessStoreEnergy || "").toString();
    setMinBessStoreEnergy(scalarVal);
    setBulkMinBessPct(scalarVal);

    const tenureVal = sim.simulation_tenure || (parentBess ? parentBess.simulation_tenure : 25);
    setYearlyMinBessStoreEnergy(buildYearlyMinBessList(tenureVal, sim.min_bess_store_energy_yearly, scalarVal));

    setWindTotalLossesPct((sim.wind_total_losses ?? sim.wind_total_losses_pct ?? sim.windTotalLossesPercent ?? "").toString());
    setMinDispatchPower((sim.min_dispatch_power ?? sim.minDispatchPower ?? "").toString());
    setBessDegradation(sim.bess_degradation != null ? Number(sim.bess_degradation) : 1);
    setAdjustingFactor((sim.adjusting_factor != null ? sim.adjusting_factor : 1).toString());

    // Pre-fill profile form values (discharge, charging & grid charging profile) immediately
    try {
      setLoadingDischarge(true);
      setLoadingCharging(true);
      setLoadingGridCharging(true);
      const [dischargeData, chargingData, gridChargingData] = await Promise.all([
        fetchHybridDischargeProfile(sim.id),
        fetchHybridChargingProfile(sim.id),
        fetchHybridGridChargingProfile(sim.id),
      ]);
      setDischargeProfile(dischargeData && dischargeData.length > 0 ? dischargeData : buildDefault24HourMatrix());
      setChargingProfile(chargingData && chargingData.length > 0 ? chargingData : buildDefault24HourMatrix());
      setGridChargingProfile(gridChargingData && gridChargingData.length > 0 ? gridChargingData : buildDefault24HourMatrix());
    } catch (err) {
      console.error(err);
      message.error("Failed to load profile configuration: " + err.message);
    } finally {
      setLoadingDischarge(false);
      setLoadingCharging(false);
      setLoadingGridCharging(false);
    }

    // Go directly to Step 1 (Form)
    setCurrentStep(1);
    setActiveTab("wizard");
    if (navigate) navigate("/hybrid-bess");
    setSimulationResults(null);
    setLoadedFromHistory(true);
  };

  const handleDownloadReport = async (simId) => {
    try {
      setDownloadingSims(prev => ({ ...prev, [simId]: true }));
      message.loading({ content: "Generating and downloading report... Please wait.", key: "download-report", duration: 0 });
      const data = await downloadHybridReport(simId);
      const hourlyProfile = data.hourly_profile || [];

      if (hourlyProfile.length === 0) {
        message.warning({ content: "No simulation records found to export.", key: "download-report" });
        setDownloadingSims(prev => ({ ...prev, [simId]: false }));
        return;
      }

      // Briefly wait to allow the message to show before synchronous XLSX execution blocks the thread
      await new Promise(resolve => setTimeout(resolve, 100));

      const worksheet = XLSX.utils.json_to_sheet(formatRowsForExcel(hourlyProfile));
      applyExcelNumberFormat(worksheet);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Simulation Report");

      XLSX.writeFile(workbook, `hybrid_simulation_${simId}_report.xlsx`);
      message.success({ content: "Report downloaded successfully!", key: "download-report" });
    } catch (err) {
      console.error(err);
      message.error({ content: "Failed to download report: " + err.message, key: "download-report" });
    } finally {
      setDownloadingSims(prev => ({ ...prev, [simId]: false }));
    }
  };

  const handleDeleteHybridSimulation = (id) => {
    modal.confirm({
      title: "Are you sure you want to delete this hybrid simulation?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          message.loading({ content: "Deleting simulation...", key: "delete-hybrid-sim" });
          await deleteHybridSimulation(id);
          message.success({ content: "Simulation deleted successfully!", key: "delete-hybrid-sim" });
          setHybridSimulations((prev) => prev.filter((sim) => sim.id !== id));
        } catch (err) {
          console.error(err);
          message.error({ content: "Failed to delete simulation: " + err.message, key: "delete-hybrid-sim" });
        }
      },
    });
  };

  const startFreshSimulation = () => {
    setCreatedSimId(null);
    setBessSizingSimId("");
    setSimulationTenure("");
    setSimulationType("1");
    setDt33kvPct("");
    setBessChargingEff("");
    setBessDischargingEff("");
    setBessIdtCapacity("");
    setBessIdtNoLoad("");
    setBessIdtLoad("");
    setWindIdtCapacity("");
    setWindIdtNoLoad("");
    setWindIdtLoad("");
    setTrans220Capacity("");
    setTrans220NoLoad("");
    setTrans220Load("");
    setTransLoss220kv("");
    setUseDirectTrans220Loss(false);
    setHas220Transformer(true);
    setTrans400Capacity("");
    setTrans400NoLoad("");
    setTrans400Load("");
    setTransLoss400kv("");
    setUseDirectTrans400Loss(false);
    setHas400Transformer(true);
    setConductorResistance220("");
    setLineDistance220("");
    setConductorResistance400("");
    setLineDistance400("");
    setTransLineLoss220kv("");
    setUseDirectLineLoss220(false);
    setHas220Line(true);
    setTransLineLoss400kv("");
    setUseDirectLineLoss400(false);
    setHas400Line(true);
    setBessUnavailability("");
    setGridUnavailability("");
    setEvacuationCapacity("");
    setBessFeederCapacity("");
    setGridFeederCapacity("");
    setSolarFeederCapacity("");
    setMinBessStoreEnergy("");
    setWindTotalLossesPct("");
    setMinDispatchPower("");
    setBessDegradation(1);
    setAdjustingFactor("1");
    setDischargeProfile([]);
    setChargingProfile([]);
    setSimulationResults(null);
    setLoadedFromHistory(false);
    setProfileFilename("");
    setCurrentStep(1);
    setProjectValues({
      name: "", container_batch: "", pcs: "", simulation_type: "closed_loop", operation_hour: 4,
      container_nos: "", pcs_nos: "", min_aug_period: "", min_aug_container: "", max_storage_capacity: "",
      start_year: "", simulation_tenure: "", energy_poi_mwh: "", purchase_years: [], use_220kv_loss: true,
      transmission_line_220kv_loss: "", conductor_resistance_220kv_ohm: "", transmission_line_220kv_distance_km: "",
      use_transformer_220_loss: true, transformer_220kv_loss: "", use_400kv_loss: false, transmission_line_400kv_loss: "",
      conductor_resistance_400kv_ohm: "", transmission_line_400kv_distance_km: "", use_transformer_400_loss: false,
      transformer_400kv_loss: "", solar_aux_power: "", ht_ac_cable_loss: "", idt_loss: "", lt_ac_cable_loss: "",
      pcs_loss: "", lt_dc_cable_loss: "", bess_plant_unavailability: "",
    });
  };

  const handleReset = () => {
    modal.confirm({
      title: "Reset Configuration?",
      content: "Are you sure you want to reset all inputs and start a new simulation draft?",
      okText: "Reset",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        startFreshSimulation();
        message.success("Configuration reset to defaults.");
      }
    });
  };

  const handleStartNewSimulation = () => {
    const modalInstance = modal.info({
      title: "Start New Simulation",
      icon: <InfoCircleOutlined />,
      footer: null,
      closable: true,
      content: (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-sm text-slate-600">Would you like to copy the current configuration for the new simulation or start fresh?</p>
          <div className="flex gap-3 justify-end mt-2">
            <button
              className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              onClick={() => {
                startFreshSimulation();
                message.success("Started a fresh simulation.");
                modalInstance.destroy();
              }}
            >
              Start Fresh
            </button>
            <button
              className="px-4 py-2 bg-[#0052cc] text-white hover:bg-[#0043a4] rounded-lg text-sm font-semibold transition-colors cursor-pointer"
              onClick={() => {
                setCreatedSimId(null);
                setProjectValues(prev => ({ ...prev, name: prev.name ? prev.name + " (Copy)" : "New Simulation" }));
                setLoadedFromHistory(false);
                message.success("Configuration copied. You can now save it as a new simulation.");
                modalInstance.destroy();
              }}
            >
              Copy Configuration
            </button>
          </div>
        </div>
      )
    });
  };

  // Preview BESS Sizing Results (Step 1)
  const handlePreviewSizing = async () => {
    if (!projectValues.name) {
      message.error("Please enter a Simulation Name before previewing.");
      return;
    }
    if (!projectValues.start_year) {
      message.error("Please enter a Start Year.");
      return;
    }
    if (!projectValues.simulation_tenure) {
      message.error("Please enter Simulation Tenure.");
      return;
    }
    if (projectValues.simulation_type === "closed_loop") {
      const augErr = validatePurchaseYears(projectValues);
      if (augErr) {
        setStep1Errors((prev) => ({ ...prev, purchase_years: augErr }));
        setStep1Touched((prev) => ({ ...prev, purchase_years: true }));
        message.error(augErr);
        return;
      }
    }

    setPreviewLoading(true);
    try {
      const bessPayload = {
        id: createdBessSizingSimId || undefined,
        name: projectValues.name,
        container_batch: projectValues.container_batch || (containerOptions[0]?.id || 1),
        pcs: projectValues.pcs || (pcsOptions[0]?.id || 1),
        simulation_type: projectValues.simulation_type || "closed_loop",
        operation_hour: Number(projectValues.operation_hour) || 4,
        container_nos: projectValues.container_nos !== "" ? Number(projectValues.container_nos) : null,
        pcs_nos: projectValues.pcs_nos !== "" ? Number(projectValues.pcs_nos) : null,
        min_aug_period: projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_period !== "" ? Number(projectValues.min_aug_period) : null,
        min_aug_container: projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_container !== "" ? Number(projectValues.min_aug_container) : null,
        max_storage_capacity: projectValues.simulation_type === "auto_closed_loop" && projectValues.max_storage_capacity !== "" ? Number(projectValues.max_storage_capacity) : null,
        start_year: Number(projectValues.start_year),
        simulation_tenure: Number(projectValues.simulation_tenure),
        energy_poi_mwh: projectValues.energy_poi_mwh !== "" ? Number(projectValues.energy_poi_mwh) : 0,
        purchase_years: projectValues.purchase_years || [],

        // System Losses
        use_220kv_loss: projectValues.use_220kv_loss,
        transmission_line_220kv_loss: projectValues.use_220kv_loss && projectValues.transmission_line_220kv_loss !== "" ? Number(projectValues.transmission_line_220kv_loss) : null,
        use_transformer_220_loss: projectValues.use_transformer_220_loss,
        transformer_220kv_loss: projectValues.use_transformer_220_loss && projectValues.transformer_220kv_loss !== "" ? Number(projectValues.transformer_220kv_loss) : null,
        use_400kv_loss: projectValues.use_400kv_loss,
        transmission_line_400kv_loss: projectValues.use_400kv_loss && projectValues.transmission_line_400kv_loss !== "" ? Number(projectValues.transmission_line_400kv_loss) : null,
        use_transformer_400_loss: projectValues.use_transformer_400_loss,
        transformer_400kv_loss: projectValues.use_transformer_400_loss && projectValues.transformer_400kv_loss !== "" ? Number(projectValues.transformer_400kv_loss) : null,
        solar_aux_power: projectValues.solar_aux_power !== "" ? Number(projectValues.solar_aux_power) : null,
        ht_ac_cable_loss: projectValues.ht_ac_cable_loss !== "" ? Number(projectValues.ht_ac_cable_loss) : null,
        idt_loss: projectValues.idt_loss !== "" ? Number(projectValues.idt_loss) : null,
        lt_ac_cable_loss: projectValues.lt_ac_cable_loss !== "" ? Number(projectValues.lt_ac_cable_loss) : null,
        pcs_loss: projectValues.pcs_loss !== "" ? Number(projectValues.pcs_loss) : null,
        lt_dc_cable_loss: projectValues.lt_dc_cable_loss !== "" ? Number(projectValues.lt_dc_cable_loss) : null,
        bess_plant_unavailability: projectValues.bess_plant_unavailability !== "" ? Number(projectValues.bess_plant_unavailability) : null,
        tl_volt_stage_1: projectValues.tl_volt_stage_1 ? Number(projectValues.tl_volt_stage_1) : 220,
        tl_volt_stage_2: projectValues.tl_volt_stage_2 ? Number(projectValues.tl_volt_stage_2) : 400,
      };

      const savedBessSim = await saveSimulation(bessPayload);
      setCreatedBessSizingSimId(savedBessSim.id);
      setBessSizingSimId(savedBessSim.id.toString());

      // Fetch the full simulation details (year_outputs, summary)
      const details = await fetchSimulationDetails(savedBessSim.id);
      setPreviewData(details);
      setShowPreview(true);
      message.success("BESS sizing results loaded for preview!");
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to generate sizing preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Step 1: Save Project Details
  const handleStep1ProjectDetailsSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!projectValues.name) {
      message.error("Please enter a Simulation Name.");
      return;
    }
    if (!projectValues.start_year) {
      message.error("Please enter a Start Year.");
      return;
    }
    if (!projectValues.simulation_tenure) {
      message.error("Please enter Simulation Tenure.");
      return;
    }

    if (projectValues.simulation_type === "closed_loop") {
      const augErr = validatePurchaseYears(projectValues);
      if (augErr) {
        setStep1Errors((prev) => ({ ...prev, purchase_years: augErr }));
        setStep1Touched((prev) => ({ ...prev, purchase_years: true }));
        message.error(augErr);
        return;
      }
    }
    setSubmitting(true);
    try {
      const bessPayload = {
        id: createdBessSizingSimId || undefined,
        name: projectValues.name,
        container_batch: projectValues.container_batch || (containerOptions[0]?.id || 1),
        pcs: projectValues.pcs || (pcsOptions[0]?.id || 1),
        simulation_type: projectValues.simulation_type || "closed_loop",
        operation_hour: Number(projectValues.operation_hour) || 4,
        container_nos: projectValues.container_nos !== "" ? Number(projectValues.container_nos) : null,
        pcs_nos: projectValues.pcs_nos !== "" ? Number(projectValues.pcs_nos) : null,
        min_aug_period: projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_period !== "" ? Number(projectValues.min_aug_period) : null,
        min_aug_container: projectValues.simulation_type === "auto_closed_loop" && projectValues.min_aug_container !== "" ? Number(projectValues.min_aug_container) : null,
        max_storage_capacity: projectValues.simulation_type === "auto_closed_loop" && projectValues.max_storage_capacity !== "" ? Number(projectValues.max_storage_capacity) : null,
        start_year: Number(projectValues.start_year),
        simulation_tenure: Number(projectValues.simulation_tenure),
        energy_poi_mwh: projectValues.energy_poi_mwh !== "" ? Number(projectValues.energy_poi_mwh) : 0,
        purchase_years: projectValues.purchase_years || [],

        // System Losses
        use_220kv_loss: projectValues.use_220kv_loss,
        transmission_line_220kv_loss: projectValues.use_220kv_loss && projectValues.transmission_line_220kv_loss !== "" ? Number(projectValues.transmission_line_220kv_loss) : null,

        use_transformer_220_loss: projectValues.use_transformer_220_loss,
        transformer_220kv_loss: projectValues.use_transformer_220_loss && projectValues.transformer_220kv_loss !== "" ? Number(projectValues.transformer_220kv_loss) : null,

        use_400kv_loss: projectValues.use_400kv_loss,
        transmission_line_400kv_loss: projectValues.use_400kv_loss && projectValues.transmission_line_400kv_loss !== "" ? Number(projectValues.transmission_line_400kv_loss) : null,

        use_transformer_400_loss: projectValues.use_transformer_400_loss,
        transformer_400kv_loss: projectValues.use_transformer_400_loss && projectValues.transformer_400kv_loss !== "" ? Number(projectValues.transformer_400kv_loss) : null,

        solar_aux_power: projectValues.solar_aux_power !== "" ? Number(projectValues.solar_aux_power) : null,
        ht_ac_cable_loss: projectValues.ht_ac_cable_loss !== "" ? Number(projectValues.ht_ac_cable_loss) : null,
        idt_loss: projectValues.idt_loss !== "" ? Number(projectValues.idt_loss) : null,
        lt_ac_cable_loss: projectValues.lt_ac_cable_loss !== "" ? Number(projectValues.lt_ac_cable_loss) : null,
        pcs_loss: projectValues.pcs_loss !== "" ? Number(projectValues.pcs_loss) : null,
        lt_dc_cable_loss: projectValues.lt_dc_cable_loss !== "" ? Number(projectValues.lt_dc_cable_loss) : null,
        bess_plant_unavailability: projectValues.bess_plant_unavailability !== "" ? Number(projectValues.bess_plant_unavailability) : null,
      };

      const savedBessSim = await saveSimulation(bessPayload);
      setCreatedBessSizingSimId(savedBessSim.id);
      setBessSizingSimId(savedBessSim.id.toString());
      setSimulationTenure(projectValues.simulation_tenure.toString());

      // Auto populate Step 2 parameters from Step 1 entries
      setHtAcCableLoss(projectValues.ht_ac_cable_loss !== "" ? projectValues.ht_ac_cable_loss.toString() : "");
      setIdtLoss(projectValues.idt_loss !== "" ? projectValues.idt_loss.toString() : "");
      setLtAcCableLoss(projectValues.lt_ac_cable_loss !== "" ? projectValues.lt_ac_cable_loss.toString() : "");
      setLtDcCableLoss(projectValues.lt_dc_cable_loss !== "" ? projectValues.lt_dc_cable_loss.toString() : "");
      setPcsLoss(projectValues.pcs_loss !== "" ? projectValues.pcs_loss.toString() : "");
      setBessUnavailability(projectValues.bess_plant_unavailability !== "" ? projectValues.bess_plant_unavailability.toString() : "");

      setHas220Transformer(projectValues.use_transformer_220_loss || Boolean(projectValues.transformer_220kv_loss));
      setUseDirectTrans220Loss(projectValues.use_transformer_220_loss);
      setTransLoss220kv(projectValues.transformer_220kv_loss !== "" ? projectValues.transformer_220kv_loss.toString() : "");

      setHas400Transformer(projectValues.use_transformer_400_loss || Boolean(projectValues.transformer_400kv_loss));
      setUseDirectTrans400Loss(projectValues.use_transformer_400_loss);
      setTransLoss400kv(projectValues.transformer_400kv_loss !== "" ? projectValues.transformer_400kv_loss.toString() : "");

      setHas220Line(projectValues.use_220kv_loss || Boolean(projectValues.transmission_line_220kv_loss));
      setUseDirectLineLoss220(projectValues.use_220kv_loss);
      setTransLineLoss220kv(projectValues.transmission_line_220kv_loss !== "" ? projectValues.transmission_line_220kv_loss.toString() : "");

      setHas400Line(projectValues.use_400kv_loss || Boolean(projectValues.transmission_line_400kv_loss));
      setUseDirectLineLoss400(projectValues.use_400kv_loss);
      setTransLineLoss400kv(projectValues.transmission_line_400kv_loss !== "" ? projectValues.transmission_line_400kv_loss.toString() : "");

      const hybridPayload = {
        bess_sizing_simulation: savedBessSim.id,
        simulation_tenure: parseInt(projectValues.simulation_tenure),
        simulation_type: parseInt(simulationType) || 1,
      };

      const savedHybridSim = await createHybridSimulation(hybridPayload, createdSimId);
      setCreatedSimId(savedHybridSim.id);

      message.success("Project details saved successfully!");
      setCurrentStep(2); // Advance to Step 2 (Parameters)
    } catch (err) {
      console.error(err);
      message.error(err.message || "Failed to save project details");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2: Save Parameters Data
  const handleStep2ParametersSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!createdSimId && !bessSizingSimId) {
      message.error("Simulation session not initialized. Please complete Step 1.");
      return;
    }

    if (
      (simulationType === "2" || simulationType === 2) &&
      (windTotalLossesPct === "" ||
        windTotalLossesPct === null ||
        windTotalLossesPct === undefined ||
        isNaN(parseFloat(windTotalLossesPct)))
    ) {
      message.error("Please enter Wind Total Losses (%).");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        bess_sizing_simulation: parseInt(bessSizingSimId || createdBessSizingSimId || 1),
        simulation_tenure: parseInt(simulationTenure || projectValues.simulation_tenure || 25),
        simulation_type: parseInt(simulationType),
        tl_volt_stage_1: projectValues.tl_volt_stage_1 ? parseInt(projectValues.tl_volt_stage_1) : 220,
        tl_volt_stage_2: projectValues.tl_volt_stage_2 ? parseInt(projectValues.tl_volt_stage_2) : 400,
        dt_33kv_pct: parseFloat(dt33kvPct),
        bess_charging_efficiency_pct: bessChargingEff ? parseFloat(bessChargingEff) : 94.53,
        bess_discharging_efficiency_pct: bessDischargingEff ? parseFloat(bessDischargingEff) : 95.01,
        bess_idt_installed_capacity_mva: bessIdtCapacity ? parseFloat(bessIdtCapacity) : 0.0,
        bess_idt_noload_losses_mva: parseFloat(bessIdtNoLoad),
        bess_idt_load_losses_mva: bessIdtLoad ? parseFloat(bessIdtLoad) : 0.0,
        wind_idt_installed_capacity_mva: (simulationType === "2" || simulationType === 2) && windIdtCapacity ? parseFloat(windIdtCapacity) : 0,
        wind_idt_noload_losses_mva: (simulationType === "2" || simulationType === 2) && windIdtNoLoad ? parseFloat(windIdtNoLoad) : 0,
        wind_idt_load_losses_mva: (simulationType === "2" || simulationType === 2) && windIdtLoad ? parseFloat(windIdtLoad) : 0,
        transformer_220kv_capacity_mva: has220Transformer && !useDirectTrans220Loss && trans220Capacity ? parseFloat(trans220Capacity) : null,
        transformer_220kv_noload_losses_mva: has220Transformer && !useDirectTrans220Loss && trans220NoLoad ? parseFloat(trans220NoLoad) : null,
        transformer_220kv_load_losses_mva: has220Transformer && !useDirectTrans220Loss && trans220Load ? parseFloat(trans220Load) : null,
        transformer_220kv_loss: has220Transformer && useDirectTrans220Loss && transLoss220kv ? parseFloat(transLoss220kv) : null,
        transformer_400kv_capacity_mva: has400Transformer && !useDirectTrans400Loss && trans400Capacity ? parseFloat(trans400Capacity) : null,
        transformer_400kv_noload_losses_mva: has400Transformer && !useDirectTrans400Loss && trans400NoLoad ? parseFloat(trans400NoLoad) : null,
        transformer_400kv_load_losses_mva: has400Transformer && !useDirectTrans400Loss && trans400Load ? parseFloat(trans400Load) : null,
        transformer_400kv_loss: has400Transformer && useDirectTrans400Loss && transLoss400kv ? parseFloat(transLoss400kv) : null,
        conductor_resistance_220kv_ohm: has220Line && !useDirectLineLoss220 && conductorResistance220 ? parseFloat(conductorResistance220) : null,
        transmission_line_220kv_distance_km: has220Line && !useDirectLineLoss220 && lineDistance220 ? parseFloat(lineDistance220) : null,
        conductor_resistance_400kv_ohm: has400Line && !useDirectLineLoss400 && conductorResistance400 ? parseFloat(conductorResistance400) : null,
        transmission_line_400kv_distance_km: has400Line && !useDirectLineLoss400 && lineDistance400 ? parseFloat(lineDistance400) : null,
        transmission_line_220kv_loss: has220Line && useDirectLineLoss220 && transLineLoss220kv ? parseFloat(transLineLoss220kv) : null,
        transmission_line_400kv_loss: has400Line && useDirectLineLoss400 && transLineLoss400kv ? parseFloat(transLineLoss400kv) : null,
        bess_unavailability: parseFloat(bessUnavailability),
        grid_unavailability: parseFloat(gridUnavailability),
        ht_ac_cable_loss: htAcCableLoss !== "" ? parseFloat(htAcCableLoss) : null,
        idt_loss: idtLoss !== "" ? parseFloat(idtLoss) : null,
        lt_ac_cable_loss: ltAcCableLoss !== "" ? parseFloat(ltAcCableLoss) : null,
        lt_dc_cable_loss: ltDcCableLoss !== "" ? parseFloat(ltDcCableLoss) : null,
        pcs_loss: pcsLoss !== "" ? parseFloat(pcsLoss) : null,
        evacuation_capacity_mva: parseFloat(evacuationCapacity),
        bess_feeder_capacity_mva: parseFloat(bessFeederCapacity),
        grid_feeder_capacity_mva: gridFeederCapacity ? parseFloat(gridFeederCapacity) : null,
        solar_feeder_capacity_mva: solarFeederCapacity ? parseFloat(solarFeederCapacity) : null,
        min_bess_store_energy: minBessStoreEnergy ? parseFloat(minBessStoreEnergy) : null,
        min_bess_store_energy_yearly: yearlyMinBessStoreEnergy.map(item => ({ year: Number(item.year), pct: Number(item.pct) })),
        wind_total_losses: (simulationType === "2" || simulationType === 2) && windTotalLossesPct ? parseFloat(windTotalLossesPct) : 0,
        wind_total_losses_pct: (simulationType === "2" || simulationType === 2) && windTotalLossesPct ? parseFloat(windTotalLossesPct) : 0,
        min_dispatch_power: (simulationType === "2" || simulationType === 2) && minDispatchPower ? parseFloat(minDispatchPower) : 0,
        bess_degradation: (simulationType === "2" || simulationType === 2) ? (parseInt(bessDegradation, 10) || 1) : 1,
        adjusting_factor: (simulationType === "2" || simulationType === 2) && Number(bessDegradation) === 1 ? (parseFloat(adjustingFactor) >= 1 ? parseFloat(adjustingFactor) : 1) : 1,
      };

      const res = await createHybridSimulation(payload, createdSimId);
      message.success("General parameters saved successfully!");
      setCreatedSimId(res.id);
      setCurrentStep(3); // Advance to Step 3 (Min BESS Stored Energy)
      loadHybridSimulations(); // Refresh history in background
    } catch (err) {
      message.error(err.message || "Failed to save parameters");
    } finally {
      setSubmitting(false);
    }
  };

  // Step 3: Save Yearly Min BESS Stored Energy
  const handleStep3YearlySubmit = async () => {
    if (!createdSimId) {
      message.error("Simulation session not initialized. Please complete Step 1 & Step 2.");
      return;
    }
    try {
      setSavingYearlyMinBess(true);
      const defaultPct = minBessStoreEnergy !== "" && minBessStoreEnergy !== null && minBessStoreEnergy !== undefined ? Number(minBessStoreEnergy) : 100;
      const yearlyPayload = yearlyMinBessStoreEnergy.map((item) => ({
        year: Number(item.year),
        pct: item.pct !== "" && item.pct !== null && item.pct !== undefined ? Number(item.pct) : defaultPct,
      }));

      await createHybridSimulation(
        {
          min_bess_store_energy: minBessStoreEnergy !== "" ? parseFloat(minBessStoreEnergy) : null,
          min_bess_store_energy_yearly: yearlyPayload,
        },
        createdSimId
      );

      message.success("Yearly Min BESS Stored Energy targets saved successfully!");
      setCurrentStep(4); // Advance to Step 4 (Discharge Schedule)
    } catch (err) {
      message.error("Failed to save Yearly Min BESS Stored Energy: " + err.message);
    } finally {
      setSavingYearlyMinBess(false);
    }
  };

  // Step 4: Save Discharge Profile
  const handleStep4DischargeSubmit = async () => {
    if (!createdSimId) return;
    try {
      setSavingDischarge(true);
      await saveHybridDischargeProfile(createdSimId, dischargeProfile);
      message.success("Discharge schedule saved successfully!");
      setCurrentStep(5); // Advance to Step 5 (Charging Schedule)
    } catch (err) {
      message.error("Failed to save discharge profile: " + err.message);
    } finally {
      setSavingDischarge(false);
    }
  };

  // Step 5: Save Charging Profile
  const handleStep5ChargingSubmit = async () => {
    if (!createdSimId) return;
    try {
      setSavingCharging(true);
      await saveHybridChargingProfile(createdSimId, chargingProfile);
      message.success("Charging schedule saved successfully!");
      setCurrentStep(6); // Advance to Step 6 (Grid Charging Schedule)
    } catch (err) {
      message.error("Failed to save charging profile: " + err.message);
    } finally {
      setSavingCharging(false);
    }
  };

  // Step 6: Save Grid Charging Profile
  const handleStep6GridChargingSubmit = async () => {
    if (!createdSimId) return;
    try {
      setSavingGridCharging(true);
      await saveHybridGridChargingProfile(createdSimId, gridChargingProfile);
      message.success("Grid charging schedule saved successfully!");
      setCurrentStep(7); // Advance to Step 7 (Upload Profile)
    } catch (err) {
      message.error("Failed to save grid charging profile: " + err.message);
    } finally {
      setSavingGridCharging(false);
    }
  };

  // Step 7: Upload Profile
  const handleStep7UploadSubmit = async () => {
    if (!uploadFile) {
      if (loadedFromHistory) {
        setCurrentStep(8); // Advance to Step 8
        return;
      }
      setUploadError(true);
      message.error("Please upload the generation profile spreadsheet to proceed.");
      return;
    }
    if (!createdSimId) {
      message.error("Simulation session not initialized. Please go back to Step 1.");
      return;
    }
    if (uploadDataType === "single" && !solarDegradation && solarDegradation !== 0) {
      message.error("Please provide the Solar Degradation Rate for single-year data.");
      return;
    }
    try {
      setUploadingProfile(true);
      setUploadError(false);
      await uploadSimulationGenerationProfile(createdSimId, solarDegradation, uploadFile);
      message.success("Simulation generation profile uploaded successfully!");
      if (uploadFile?.name) {
        setProfileFilename(uploadFile.name);
      }
      setUploadFile(null);
      setCurrentStep(8); // Advance to Step 8 (Run & Results)
    } catch (err) {
      message.error("Upload failed: " + err.message);
    } finally {
      setUploadingProfile(false);
    }
  };

  // Step 4: Run Simulation
  const handleRunSimulation = async () => {
    if (!createdSimId) return;
    try {
      setRunningSim(true);
      await runHybridSimulation(createdSimId);
      message.success("Hybrid simulation run completed successfully!");
      navigate(`/hybrid-bess/${createdSimId}`);
    } catch (err) {
      message.error("Simulation run failed: " + err.message);
    } finally {
      setRunningSim(false);
    }
  };

  const handleLoadHourlyDetails = async (year) => {
    if (!createdSimId) return;
    try {
      setLoadingHourlyData(true);
      setSelectedDetailsYear(year);
      const res = await fetchHybridSimulationHourlyProfile(createdSimId, year);
      setHourlyProfileData(res.hourly_profile);
      setSelectedChartDay(1);
    } catch (err) {
      message.error("Failed to load hourly details: " + err.message);
    } finally {
      setLoadingHourlyData(false);
    }
  };

  const handleToggleCell = (hour, monthKey) => {
    setDischargeProfile((prev) =>
      prev.map((row) =>
        row.hour === hour ? { ...row, [monthKey]: !row[monthKey] } : row
      )
    );
  };

  const handleToggleRow = (hour, isChecked) => {
    setDischargeProfile((prev) =>
      prev.map((row) => {
        if (row.hour === hour) {
          const updated = { ...row };
          MONTHS.forEach((m) => {
            updated[m.key] = isChecked;
          });
          return updated;
        }
        return row;
      })
    );
  };

  const handleToggleColumn = (monthKey, isChecked) => {
    setDischargeProfile((prev) =>
      prev.map((row) => ({
        ...row,
        [monthKey]: isChecked,
      }))
    );
  };

  const handleToggleAllMatrix = (isChecked) => {
    setDischargeProfile((prev) =>
      prev.map((row) => {
        const updated = { ...row };
        MONTHS.forEach((m) => {
          updated[m.key] = isChecked;
        });
        return updated;
      })
    );
  };

  const isAllMatrixChecked =
    dischargeProfile.length > 0 &&
    dischargeProfile.every((row) => MONTHS.every((m) => row[m.key] === true));

  const handleToggleChargingCell = (hour, monthKey) => {
    setChargingProfile((prev) =>
      prev.map((row) =>
        row.hour === hour ? { ...row, [monthKey]: !row[monthKey] } : row
      )
    );
  };

  const handleToggleChargingRow = (hour, isChecked) => {
    setChargingProfile((prev) =>
      prev.map((row) => {
        if (row.hour === hour) {
          const updated = { ...row };
          MONTHS.forEach((m) => {
            updated[m.key] = isChecked;
          });
          return updated;
        }
        return row;
      })
    );
  };

  const handleToggleChargingColumn = (monthKey, isChecked) => {
    setChargingProfile((prev) =>
      prev.map((row) => ({
        ...row,
        [monthKey]: isChecked,
      }))
    );
  };

  const handleToggleChargingAllMatrix = (isChecked) => {
    setChargingProfile((prev) =>
      prev.map((row) => {
        const updated = { ...row };
        MONTHS.forEach((m) => {
          updated[m.key] = isChecked;
        });
        return updated;
      })
    );
  };

  const isAllChargingMatrixChecked =
    chargingProfile.length > 0 &&
    chargingProfile.every((row) => MONTHS.every((m) => row[m.key] === true));

  const handleToggleGridChargingCell = (hour, monthKey) => {
    setGridChargingProfile((prev) =>
      prev.map((row) =>
        row.hour === hour ? { ...row, [monthKey]: !row[monthKey] } : row
      )
    );
  };

  const handleToggleGridChargingRow = (hour, isChecked) => {
    setGridChargingProfile((prev) =>
      prev.map((row) => {
        if (row.hour === hour) {
          const updated = { ...row };
          MONTHS.forEach((m) => {
            updated[m.key] = isChecked;
          });
          return updated;
        }
        return row;
      })
    );
  };

  const handleToggleGridChargingColumn = (monthKey, isChecked) => {
    setGridChargingProfile((prev) =>
      prev.map((row) => ({
        ...row,
        [monthKey]: isChecked,
      }))
    );
  };

  const handleToggleGridChargingAllMatrix = (isChecked) => {
    setGridChargingProfile((prev) =>
      prev.map((row) => {
        const updated = { ...row };
        MONTHS.forEach((m) => {
          updated[m.key] = isChecked;
        });
        return updated;
      })
    );
  };

  const isAllGridChargingMatrixChecked =
    gridChargingProfile.length > 0 &&
    gridChargingProfile.every((row) => MONTHS.every((m) => row[m.key] === true));

  const activeDayData = hourlyProfileData
    .filter((row) => row.day === selectedChartDay)
    .map((row) => ({
      ...row,
      solar_gen_33kv_mw: row.solar_gen_33kv_mw || 0,
      solar_to_bess_power: row.solar_to_bess_power ?? row.power_for_bess_charging_at_33kv_bus ?? 0,
      solar_power_at_poi: row.solar_power_at_poi ?? row.export_solar_power ?? 0,
      grid_import_at_poi: row.grid_import_at_poi ?? row.grid_import_at_400kv_bus ?? row.grid_import_for_bess_charging ?? 0,
      bess_power_at_poi: row.bess_power_at_poi ?? row.bess_power_at_400kv_bus ?? row.bess_power_at_220kv_bus ?? 0,
    }));

  return (
    <div className="bess-container-redesign min-h-screen pt-2 px-6 pb-6 md:pt-4 md:px-8 md:pb-8 flex flex-col">
      {/* Scoped CSS block */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Hanken+Grotesk:ital,wght@0,100..900;1,100..900&family=Geist:wght@100..900&family=Inter:ital,wght@0,100..900;1,100..900&display=swap');

        .bess-container-redesign {
          background-color: #f7f9fb !important;
          font-family: 'Inter', sans-serif !important;
          color: #191c1e !important;
        }

        .bess-container-redesign h1,
        .bess-container-redesign h2,
        .bess-container-redesign h3,
        .bess-container-redesign h4 {
          font-family: 'Hanken Grotesk', sans-serif !important;
          font-weight: 600 !important;
          border-left: none !important;
          padding-left: 0 !important;
          text-transform: none !important;
          margin: 0 !important;
          letter-spacing: -0.01em !important;
        }

        .bess-container-redesign h4.form-section-title {
          font-size: 16px !important;
          font-weight: 700 !important;
        }

        .bess-container-redesign .headline-lg {
          font-size: 30px !important;
          line-height: 38px !important;
          letter-spacing: -0.02em !important;
        }

        .bess-layout-content .bess-container-redesign label {
          color: #0c2340 !important;
          font-family: 'Inter', sans-serif !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          margin-bottom: 8px !important;
          display: block !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]) {
          height: 44px !important;
          padding: 0 14px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 400 !important;
          color: #191c1e !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          background-color: #ffffff !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
          box-sizing: border-box !important;
          width: 100% !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):hover {
          border-color: #707881 !important;
        }

        .bess-layout-content .bess-container-redesign input:not([type="checkbox"]):not([type="radio"]):focus {
          border-color: #0052CC !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
          outline: none !important;
        }

        .bess-card {
          background-color: #ffffff !important;
          border-radius: 10px !important;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.02) !important;
          padding: 32px !important;
          border: 1px solid #eceef0 !important;
          box-sizing: border-box !important;
        }

        /* Ant Design Dropdown custom overrides */
        .bess-container-redesign .bess-antd-select.ant-select {
          height: 44px !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select .ant-select-selector {
          background-color: #ffffff !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 44px !important;
          display: flex !important;
          align-items: center !important;
          transition: all 0.2s ease-in-out !important;
          box-shadow: none !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select .ant-select-arrow {
          top: 50% !important;
          margin-top: 0 !important;
          transform: translateY(-50%) !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select:hover .ant-select-selector {
          border-color: #707881 !important;
        }

        .bess-container-redesign .bess-antd-select.ant-select-focused .ant-select-selector {
          border-color: #0052CC !important;
          box-shadow: 0 0 0 3px #dbeafe !important;
        }

        .ant-select-dropdown {
          border-radius: 12px !important;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.04) !important;
          border: 1px solid #f1f5f9 !important;
          padding: 6px !important;
          z-index: 10000 !important;
        }

        .ant-select-dropdown .ant-select-item {
          border-radius: 6px !important;
          padding: 8px 12px !important;
          font-size: 14px !important;
          font-family: 'Inter', sans-serif !important;
        }

        .ant-select-dropdown .ant-select-item-option-selected {
          background-color: #f0f9ff !important;
          color: #0052CC !important;
          font-weight: 600 !important;
        }

        /* Stepper design matching mockups */
        .bess-stepper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          margin: 0 auto 28px auto;
          user-select: none;
          padding: 0 2px;
        }

        .bess-step-item {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          flex-shrink: 0;
        }

        .bess-step-circle {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
          transition: all 0.3s ease;
          flex-shrink: 0;
          text-align: center;
        }

        .bess-step-circle.active {
          background-color: #0052CC !important;
          color: #ffffff !important;
        }

        .bess-step-circle.completed {
          background-color: #e0f2fe !important;
          color: #0052CC !important;
        }

        .bess-step-circle.inactive {
          background-color: #d8dadc !important;
          color: #707881 !important;
        }

        .bess-step-label {
          font-size: 11px;
          font-weight: 600;
          line-height: 1;
          white-space: nowrap;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .bess-step-label.active {
          color: #0052CC !important;
        }

        .bess-step-label.inactive {
          color: #707881 !important;
        }

        .bess-step-line {
          flex: 1 1 0%;
          height: 2px;
          background-color: #d8dadc;
          margin: 0 6px;
          min-width: 8px;
          align-self: center;
          transition: all 0.3s ease;
        }

        .bess-step-line.active {
          background-color: #0052CC;
        }

        .bess-btn-primary {
          background-color: #0052CC !important;
          color: #ffffff !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          border: none !important;
          border-radius: 8px !important;
          height: 44px !important;
          padding: 0 24px !important;
          cursor: pointer !important;
          transition: background-color 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-primary:hover {
          background-color: #0073e6 !important;
        }

        .bess-btn-secondary {
          background-color: #ffffff !important;
          color: #707881 !important;
          font-family: 'Inter', sans-serif !important;
          font-weight: 500 !important;
          font-size: 14px !important;
          border: 1px solid #bfc7d2 !important;
          border-radius: 8px !important;
          height: 44px !important;
          padding: 0 20px !important;
          cursor: pointer !important;
          transition: all 0.2s ease-in-out !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .bess-btn-secondary:hover {
          border-color: #707881 !important;
          color: #191c1e !important;
        }

        .tech-badge {
          font-family: 'Geist', monospace !important;
          font-size: 10px !important;
          font-weight: 600 !important;
          background-color: #f2f4f6 !important;
          color: #707881 !important;
          padding: 2px 6px !important;
          border-radius: 4px !important;
          text-transform: uppercase !important;
          display: inline-block;
          margin-left: 6px;
        }

        .geist-val {
          font-family: 'Geist', monospace !important;
          font-weight: 500 !important;
        }

        .bess-table {
          width: 100% !important;
          border-collapse: collapse !important;
        }

        .bess-table th {
          background-color: #f8fafc !important;
          font-family: 'Geist', sans-serif !important;
          font-size: 11px !important;
          font-weight: 500 !important;
          color: #707881 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          padding: 10px 16px !important;
          border-bottom: 1px solid #e6e8ea !important;
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }

        .bess-table td {
          padding: 12px 16px !important;
          border-bottom: 1px solid #f2f4f6 !important;
          font-size: 13.5px !important;
          color: #191c1e !important;
        }

        input::placeholder {
          color: #94a3b8 !important;
          opacity: 0.55 !important;
        }
      `}</style>

      <div className="max-w-[1280px] mx-auto w-full flex-1">
        {/* Header Title Block with Inline Tabs */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-[#bfc7d2] pb-4 gap-4 mb-6">
          <h1 className="headline-lg font-bold text-[#0c2340]">
            Hybrid BESS Solar Simulation
          </h1>
          <div className="flex items-center gap-3">
            {loadedFromHistory && (
              <button
                type="button"
                onClick={handleStartNewSimulation}
                className="h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100/50 shadow-sm flex items-center justify-center gap-1.5"
                title="Start a new simulation from scratch or copy the current one"
              >
                Start New Simulation
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setActiveTab("wizard");
                navigate("/hybrid-bess");
              }}
              className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${activeTab === "wizard"
                ? "border border-blue-500 text-blue-600"
                : "border border-slate-200 text-slate-600 hover:text-slate-800"
                }`}
            >
              Simulation
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab("history");
                navigate("/hybrid-bess/history");
              }}
              className={`h-10 px-5 text-xs font-bold rounded-lg transition-all cursor-pointer bg-white shadow-sm ${activeTab === "history"
                ? "border border-blue-500 text-blue-600"
                : "border border-slate-200 text-slate-600 hover:text-slate-800"
                }`}
            >
              History ({hybridSimulations.length})
            </button>
          </div>
        </div>

        {activeTab === "wizard" ? (
          <div className="space-y-6">
            {/* Stepper Header Progress Indicator */}
            <div className="bess-stepper">
              {[
                { number: 1, title: "Project Input", desc: "Define core components, timeframes, targets, and initial system losses." },
                { number: 2, title: "Hybrid Input", desc: "Define loss factors, transformer capacities, and unavailability limits." },
                { number: 3, title: "Min BESS Stored Energy", desc: "Set year-wise minimum BESS stored energy percentages across simulation tenure." },
                { number: 4, title: "Discharge Schedule", desc: "Interactive toggle for yearly active battery discharge schedule." },
                { number: 5, title: "Charging Schedule", desc: "Interactive toggle for yearly active battery charging schedule." },
                { number: 6, title: "Grid Charging Schedule", desc: "Interactive toggle for yearly active battery grid charging schedule." },
                { number: 7, title: "Upload Profile", desc: "Upload generation profile spreadsheet and set solar degradation." },
                { number: 8, title: "Run & Results", desc: "Execute the simulation model and analyze chronological outputs." }
              ].map((s, index, stepsList) => (
                <React.Fragment key={s.number}>
                  <div className="bess-step-item">
                    <div className={`bess-step-circle ${currentStep === s.number
                      ? "active"
                      : currentStep > s.number
                        ? "completed"
                        : "inactive"
                      }`}>
                      {currentStep > s.number ? "✓" : s.number}
                    </div>
                    <span className={`bess-step-label ${currentStep === s.number
                      ? "active"
                      : "inactive"
                      }`}>
                      {s.title}
                    </span>
                  </div>
                  {index < stepsList.length - 1 && (
                    <div className={`bess-step-line ${currentStep > s.number ? "active" : ""
                      }`} />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Active Step Form Card */}
            <div className="bess-card animate-fade-in">
              <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
                <div className="flex-1">
                  <span className="text-xs font-bold text-[#0052CC] tracking-wide block mb-1">
                    Step {currentStep} of 8
                  </span>
                  <h2 className="text-2xl font-bold text-slate-800 mb-1">
                    {
                      [
                        { number: 1, title: "Project Input", desc: "Define core components, timeframes, targets, and initial system losses." },
                        { number: 2, title: "Hybrid Input", desc: "Define loss factors, transformer capacities, and unavailability limits." },
                        { number: 3, title: "Min BESS Stored Energy", desc: "Set year-wise minimum BESS stored energy percentages across simulation tenure." },
                        { number: 4, title: "Discharge Schedule", desc: "Interactive toggle for yearly active battery discharge schedule." },
                        { number: 5, title: "Charging Schedule", desc: "Interactive toggle for yearly active battery charging schedule." },
                        { number: 6, title: "Grid Charging Schedule", desc: "Interactive toggle for yearly active battery grid charging schedule." },
                        { number: 7, title: "Upload Profile", desc: "Upload generation profile spreadsheet and set solar degradation." },
                        { number: 8, title: "Run & Results", desc: "Execute the simulation model and analyze chronological outputs." }
                      ][currentStep - 1].title
                    }
                  </h2>
                  <p className="text-xs text-slate-400 font-medium">
                    {
                      [
                        { number: 1, title: "Project Input", desc: "Define core components, timeframes, targets, and initial system losses." },
                        { number: 2, title: "Hybrid Input", desc: "Define loss factors, transformer capacities, and unavailability limits." },
                        { number: 3, title: "Min BESS Stored Energy", desc: "Set year-wise minimum BESS stored energy percentages across simulation tenure." },
                        { number: 4, title: "Discharge Schedule", desc: "Interactive toggle for yearly active battery discharge schedule." },
                        { number: 5, title: "Charging Schedule", desc: "Interactive toggle for yearly active battery charging schedule." },
                        { number: 6, title: "Grid Charging Schedule", desc: "Interactive toggle for yearly active battery grid charging schedule." },
                        { number: 7, title: "Upload Profile", desc: "Upload generation profile spreadsheet and set solar degradation." },
                        { number: 8, title: "Run & Results", desc: "Execute the simulation model and analyze chronological outputs." }
                      ][currentStep - 1].desc
                    }
                  </p>
                </div>
                {currentStep === 1 && (
                  <div className="flex items-center gap-2 pt-1">
                    <Select
                      placeholder="Select Past Hybrid Simulation..."
                      value={createdSimId || undefined}
                      onChange={(simId) => {
                        if (!simId) return;
                        const matched = hybridSimulations.find((s) => s.id === simId);
                        if (matched) handleLoadSimulation(matched);
                      }}
                      allowClear
                      onClear={() => window.location.reload()}
                      style={{ minWidth: 280 }}
                      className="bess-antd-select"
                      loading={loadingList}
                      options={[...hybridSimulations]
                        .sort((a, b) => {
                          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                          if (timeB !== timeA) return timeB - timeA;
                          return (Number(b.id) || 0) - (Number(a.id) || 0);
                        })
                        .map((s) => ({
                          value: s.id,
                          label: `${getHybridSimName(s)} (${s.simulation_tenure || 25} Yrs)`
                        }))}
                    />
                  </div>
                )}
              </div>

              {currentStep === 1 && (
                <form onSubmit={handleStep1ProjectDetailsSubmit} className="space-y-6 animate-fade-in">
                  {/* Core Components */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    <div className="flex flex-col gap-1 md:col-span-2">
                      <label>SIMULATION NAME</label>
                      <input
                        type="text"
                        value={projectValues.name || ""}
                        onChange={(e) => setProjectValues({ ...projectValues, name: e.target.value })}
                        placeholder="Enter Project Name..."
                        required
                      />
                    </div>

                    <div className="flex flex-col gap-1">
                      <label>CONTAINER</label>
                      <Select
                        value={projectValues.container_batch || undefined}
                        disabled={loadingDropdowns}
                        onChange={(val) => setProjectValues({ ...projectValues, container_batch: val })}
                        placeholder="Select Container..."
                        className="bess-antd-select w-full"
                      >
                        {containerOptions.map((opt) => (
                          <Select.Option key={opt.id} value={opt.id}>
                            {opt.oem} - {parseFloat(opt.rated_capacity_mwh).toFixed(3)} MWh
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label>PCS</label>
                      <Select
                        value={projectValues.pcs || undefined}
                        disabled={loadingDropdowns}
                        onChange={(val) => setProjectValues({ ...projectValues, pcs: val })}
                        placeholder="Select PCS System..."
                        className="bess-antd-select w-full"
                      >
                        {pcsOptions.map((opt) => (
                          <Select.Option key={opt.id} value={opt.id}>
                            {opt.oem} - {opt.model}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label>SIMULATION TYPE</label>
                      <Select
                        value={projectValues.simulation_type}
                        onChange={(val) => {
                          setProjectValues({ ...projectValues, simulation_type: val });
                          if (val !== "closed_loop") {
                            setStep1Errors((prev) => ({ ...prev, purchase_years: null }));
                          } else if (step1Touched.purchase_years) {
                            const err = validatePurchaseYears({ ...projectValues, simulation_type: val });
                            setStep1Errors((prev) => ({ ...prev, purchase_years: err }));
                          }
                        }}
                        className="bess-antd-select w-full"
                      >
                        <Select.Option value="closed_loop">Manual Battery Augmentation</Select.Option>
                        <Select.Option value="open_loop">Without Battery Augmentation</Select.Option>
                        <Select.Option value="auto_closed_loop">Auto Battery Augmentation</Select.Option>
                      </Select>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label>STORAGE DURATION (HOURS)</label>
                      <input
                        type="number"
                        value={projectValues.operation_hour}
                        onChange={(e) => setProjectValues({ ...projectValues, operation_hour: e.target.value === "" ? "" : Number(e.target.value) })}
                        min="1"
                        max="24"
                        placeholder="4"
                      />
                    </div>
                  </div>

                  {/* Equipment Quantities */}
                  {projectValues.simulation_type !== "auto_closed_loop" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                      <div className="flex flex-col gap-1">
                        <label>INITIAL CONTAINER QUANTITY (NOS)</label>
                        <input
                          type="number"
                          value={projectValues.container_nos}
                          onChange={(e) => setProjectValues({ ...projectValues, container_nos: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Enter initial container quantity..."
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>PCS QUANTITY (NOS)</label>
                        <input
                          type="number"
                          value={projectValues.pcs_nos}
                          onChange={(e) => setProjectValues({ ...projectValues, pcs_nos: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Enter PCS quantity..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Simulation Settings */}
                  <div className="mt-8 space-y-4">
                    <h4 className="form-section-title text-base font-bold text-slate-800 uppercase tracking-wider pb-1">
                      Simulation Settings
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="flex flex-col gap-1">
                        <label>START YEAR</label>
                        <input
                          type="number"
                          value={projectValues.start_year}
                          onChange={(e) => setProjectValues({ ...projectValues, start_year: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Enter Start Year (e.g. 2026)..."
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>SIMULATION TENURE (YEARS)</label>
                        <input
                          type="number"
                          value={projectValues.simulation_tenure}
                          onChange={(e) => {
                            const val = e.target.value === "" ? "" : Number(e.target.value);
                            setProjectValues({ ...projectValues, simulation_tenure: val });
                            setSimulationTenure(val.toString());
                          }}
                          placeholder="Enter Simulation Tenure (e.g. 25)..."
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>CONTRACTED ENERGY @ POI (MWH)</label>
                        <input
                          type="number"
                          value={projectValues.energy_poi_mwh}
                          onChange={(e) => setProjectValues({ ...projectValues, energy_poi_mwh: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Enter energy @ POI..."
                        />
                      </div>
                    </div>

                    {/* Manual Augmentation Schedule Editor */}
                    {projectValues.simulation_type === "closed_loop" && (
                      <div className="border-t border-slate-100 pt-5 space-y-4">
                        <h4 className="font-bold text-slate-800 text-[14px] flex items-center gap-1.5">
                          <SettingOutlined className="text-[#0052CC]" />
                          Manual Augmentation Schedule Editor
                        </h4>
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Specify the project years and the number of containers to be purchased in those years.
                        </p>

                        <div className="space-y-3">
                          {(projectValues.purchase_years || []).map((item, idx) => {
                            const currentYear = typeof item === 'object' && item !== null ? item.year : item;
                            const currentContainers = typeof item === 'object' && item !== null ? item.containers : "";

                            return (
                              <div key={idx} className="flex items-end gap-4 py-2">
                                <div className="flex-1 flex flex-col gap-1">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase">Project Year</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max={projectValues.simulation_tenure || 50}
                                    value={currentYear ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? "" : Number(e.target.value);
                                      const updated = [...(projectValues.purchase_years || [])];
                                      if (typeof updated[idx] === 'object' && updated[idx] !== null) {
                                        updated[idx] = { ...updated[idx], year: val };
                                      } else {
                                        updated[idx] = { year: val, containers: 1 };
                                      }
                                      handlePurchaseYearsChange(updated);
                                    }}
                                    placeholder="Year e.g. 4"
                                    className="h-9 px-3 border border-slate-200 rounded-md text-xs w-full bg-white"
                                  />
                                </div>
                                <div className="flex-1 flex flex-col gap-1">
                                  <label className="text-[10px] font-bold text-slate-500 uppercase">Container Quantity</label>
                                  <input
                                    type="number"
                                    min="1"
                                    value={currentContainers ?? ""}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? "" : Number(e.target.value);
                                      const updated = [...(projectValues.purchase_years || [])];
                                      if (typeof updated[idx] === 'object' && updated[idx] !== null) {
                                        updated[idx] = { ...updated[idx], containers: val };
                                      } else {
                                        updated[idx] = { year: 1, containers: val };
                                      }
                                      handlePurchaseYearsChange(updated);
                                    }}
                                    placeholder="Containers e.g. 2"
                                    className="h-9 px-3 border border-slate-200 rounded-md text-xs w-full bg-white"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = (projectValues.purchase_years || []).filter((_, i) => i !== idx);
                                    handlePurchaseYearsChange(updated);
                                  }}
                                  className="h-9 px-3 bg-red-50 text-red-600 rounded-md text-xs font-semibold hover:bg-red-100 transition-colors flex items-center gap-1 cursor-pointer"
                                >
                                  <DeleteOutlined /> Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <button
                          type="button"
                          onClick={() => {
                            const current = projectValues.purchase_years || [];
                            let nextYear = 1;
                            if (current.length > 0) {
                              const lastItem = current[current.length - 1];
                              const lastYear = typeof lastItem === 'object' && lastItem !== null ? lastItem.year : lastItem;
                              if (typeof lastYear === 'number' && !isNaN(lastYear)) {
                                nextYear = lastYear + 1;
                              }
                            }
                            handlePurchaseYearsChange([...current, { year: nextYear, containers: 1 }]);
                          }}
                          className="h-9 px-4 border border-dashed border-[#0052cc] text-[#0052cc] hover:bg-blue-50/50 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer bg-white"
                        >
                          <PlusOutlined /> Add Augmentation
                        </button>

                        {step1Touched.purchase_years && step1Errors.purchase_years && (
                          <span className="text-[10px] text-red-500 font-semibold mt-1 block">
                            {step1Errors.purchase_years}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Auto Augmentation Parameters */}
                  {projectValues.simulation_type === "auto_closed_loop" && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                      <div className="flex flex-col gap-1">
                        <label>MIN AUGMENTATION PERIOD (YEARS)</label>
                        <input
                          type="number"
                          value={projectValues.min_aug_period}
                          onChange={(e) => setProjectValues({ ...projectValues, min_aug_period: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Min aug period..."
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>MIN AUGMENTATION CONTAINER QTY (NOS)</label>
                        <input
                          type="number"
                          value={projectValues.min_aug_container}
                          onChange={(e) => setProjectValues({ ...projectValues, min_aug_container: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="Min aug container qty..."
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>MAX STORAGE CAPACITY (%)</label>
                        <input
                          type="number"
                          value={projectValues.max_storage_capacity}
                          onChange={(e) => setProjectValues({ ...projectValues, max_storage_capacity: e.target.value === "" ? "" : Number(e.target.value) })}
                          placeholder="e.g. 150"
                        />
                      </div>
                    </div>
                  )}

                  {/* System Losses Settings (Matches Normal BESS exactly) */}
                  <div className="mt-8 space-y-6 border-t border-slate-100 pt-6">
                    <h4 className="form-section-title text-base font-bold text-slate-800 uppercase tracking-wider pb-1">
                      System Losses Settings
                    </h4>

                    <div className="flex flex-col md:flex-row gap-6 mb-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 1 (kV)</label>
                        <div className="flex gap-4 items-center">
                          {[66, 110, 132, 220].map((val) => (
                            <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name="step1_tl_volt_stage_1"
                                value={val}
                                checked={(projectValues.tl_volt_stage_1 || 220) === val}
                                onChange={() => setProjectValues({ ...projectValues, tl_volt_stage_1: val })}
                                className="w-4 h-4 accent-[#0052cc]"
                              />
                              <span className="text-sm text-slate-700 font-medium">{val}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="w-px bg-slate-200 hidden md:block"></div>
                      <div className="flex flex-col gap-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voltage Stage 2 (kV)</label>
                        <div className="flex gap-4 items-center">
                          {[220, 400].map((val) => (
                            <label key={val} className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="radio"
                                name="step1_tl_volt_stage_2"
                                value={val}
                                checked={(projectValues.tl_volt_stage_2 || 400) === val}
                                onChange={() => setProjectValues({ ...projectValues, tl_volt_stage_2: val })}
                                className="w-4 h-4 accent-[#0052cc]"
                              />
                              <span className="text-sm text-slate-700 font-medium">{val}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Stage 1 Transmission Line Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center gap-1.5 h-4 mb-1">
                          <input
                            type="checkbox"
                            checked={projectValues.use_220kv_loss}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProjectValues({
                                ...projectValues,
                                use_220kv_loss: checked,
                                transmission_line_220kv_loss: checked ? projectValues.transmission_line_220kv_loss : "",
                              });
                            }}
                            className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                          />
                          <span className="text-xs font-semibold select-none text-[#0c2340]">
                            {projectValues.tl_volt_stage_1 || 220}kV Transmission Line Loss (%)
                          </span>
                        </div>
                        <input
                          type="number" step="0.01"
                          disabled={!projectValues.use_220kv_loss}
                          value={projectValues.transmission_line_220kv_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, transmission_line_220kv_loss: e.target.value })}
                          placeholder="0.50"
                          className={!projectValues.use_220kv_loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                        />
                      </div>

                      {/* Stage 1 Transformer Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center gap-1.5 h-4 mb-1">
                          <input
                            type="checkbox"
                            checked={projectValues.use_transformer_220_loss}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProjectValues({
                                ...projectValues,
                                use_transformer_220_loss: checked,
                                transformer_220kv_loss: checked ? projectValues.transformer_220kv_loss : "",
                              });
                            }}
                            className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                          />
                          <span className="text-xs font-semibold select-none text-[#0c2340]">
                            {projectValues.tl_volt_stage_1 || 220}/33kV Transformer Loss (%)
                          </span>
                        </div>
                        <input
                          type="number" step="0.01"
                          disabled={!projectValues.use_transformer_220_loss}
                          value={projectValues.transformer_220kv_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, transformer_220kv_loss: e.target.value })}
                          placeholder="0.15"
                          className={!projectValues.use_transformer_220_loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                        />
                      </div>

                      {/* Stage 2 Transmission Line Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center gap-1.5 h-4 mb-1">
                          <input
                            type="checkbox"
                            checked={projectValues.use_400kv_loss}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProjectValues({
                                ...projectValues,
                                use_400kv_loss: checked,
                                transmission_line_400kv_loss: checked ? projectValues.transmission_line_400kv_loss : "",
                              });
                            }}
                            className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                          />
                          <span className="text-xs font-semibold select-none text-[#0c2340]">
                            {projectValues.tl_volt_stage_2 || 400}kV Transmission Line Loss (%)
                          </span>
                        </div>
                        <input
                          type="number" step="0.01"
                          disabled={!projectValues.use_400kv_loss}
                          value={projectValues.transmission_line_400kv_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, transmission_line_400kv_loss: e.target.value })}
                          placeholder="0.50"
                          className={!projectValues.use_400kv_loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                        />
                      </div>

                      {/* Stage 2 Transformer Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center gap-1.5 h-4 mb-1">
                          <input
                            type="checkbox"
                            checked={projectValues.use_transformer_400_loss}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setProjectValues({
                                ...projectValues,
                                use_transformer_400_loss: checked,
                                transformer_400kv_loss: checked ? projectValues.transformer_400kv_loss : "",
                              });
                            }}
                            className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                          />
                          <span className="text-xs font-semibold select-none text-[#0c2340]">
                            {projectValues.tl_volt_stage_2 || 400}/{projectValues.tl_volt_stage_1 || 220}kV Transformer Loss (%)
                          </span>
                        </div>
                        <input
                          type="number" step="0.01"
                          disabled={!projectValues.use_transformer_400_loss}
                          value={projectValues.transformer_400kv_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, transformer_400kv_loss: e.target.value })}
                          placeholder="0.15"
                          className={!projectValues.use_transformer_400_loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                        />
                      </div>

                      {/* Cable & Equipment Losses */}
                      <div className="flex flex-col gap-1">
                        <label>SOLAR AUX POWER (KW)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.solar_aux_power}
                          onChange={(e) => setProjectValues({ ...projectValues, solar_aux_power: e.target.value })}
                          placeholder="1.0"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>HT AC CABLE LOSS (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.ht_ac_cable_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, ht_ac_cable_loss: e.target.value })}
                          placeholder="0.10"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>IDT LOSS (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.idt_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, idt_loss: e.target.value })}
                          placeholder="1.00"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>LT AC CABLE LOSS (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.lt_ac_cable_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, lt_ac_cable_loss: e.target.value })}
                          placeholder="0.33"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>PCS LOSS (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.pcs_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, pcs_loss: e.target.value })}
                          placeholder="1.50"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>LT DC CABLE LOSS (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.lt_dc_cable_loss}
                          onChange={(e) => setProjectValues({ ...projectValues, lt_dc_cable_loss: e.target.value })}
                          placeholder="0.12"
                        />
                      </div>

                      <div className="flex flex-col gap-1">
                        <label>BESS PLANT AVAILABILITY (%)</label>
                        <input
                          type="number" step="0.01"
                          value={projectValues.bess_plant_unavailability}
                          onChange={(e) => setProjectValues({ ...projectValues, bess_plant_unavailability: e.target.value })}
                          placeholder="1.50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Step 1 BESS Sizing Preview Section */}
                  {showPreview && previewData && (
                    <div className="mt-8 pt-6 border-t border-slate-200 space-y-6 animate-fade-in bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-200">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                            <h4 className="font-bold text-slate-800 text-sm uppercase tracking-wider">
                              BESS Sizing Graphs Preview
                            </h4>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Evaluating configuration based on {Number(projectValues.energy_poi_mwh || 0).toFixed(1)} MWh POI Contract Target across {projectValues.simulation_tenure || 25} years.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowPreview(false)}
                          className="h-8 px-3 text-slate-500 hover:text-slate-800 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer self-start sm:self-auto shadow-sm"
                        >
                          <EyeInvisibleOutlined /> Hide Preview
                        </button>
                      </div>

                      {/* Breach Alert if any */}
                      {previewData.summary?.years_below_contract && previewData.summary.years_below_contract.length > 0 && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-xs font-semibold flex flex-col gap-1 text-red-700 shadow-sm">
                          <span className="font-bold flex items-center gap-1.5">
                            <WarningOutlined className="text-red-500" />
                            Diagnostics Alert: Contract Breach Detected
                          </span>
                          <span className="text-red-600 font-medium">
                            POI delivered energy falls below contracted target in years:{" "}
                            {previewData.summary.years_below_contract.map((yr) => `Year ${yr}`).join(", ")}
                          </span>
                        </div>
                      )}

                      {/* Graphs Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                        <div className="flex flex-col bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                          <PoiDeliveredChart
                            data={previewData.year_outputs}
                            targetVal={parseFloat(projectValues.energy_poi_mwh || 0)}
                          />
                        </div>
                        <div className="flex flex-col bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                          <AugmentationChart
                            data={previewData.year_outputs}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-slate-100 mt-8 gap-4">
                    <button
                      type="button"
                      onClick={handlePreviewSizing}
                      disabled={previewLoading || submitting}
                      className="h-10 px-5 border border-sky-500 text-sky-700 bg-sky-50/60 hover:bg-sky-100 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {previewLoading ? (
                        <>
                          <LoadingOutlined className="animate-spin" /> Simulating Sizing...
                        </>
                      ) : (
                        <>
                          <EyeOutlined /> Preview BESS Results
                        </>
                      )}
                    </button>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="bess-btn-primary"
                    >
                      {submitting ? (
                        <>
                          <LoadingOutlined className="animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          Save & Continue →
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {currentStep === 2 && (
                <form onSubmit={handleStep2ParametersSubmit} className="space-y-6">
                  {/* General Simulation Linkage (Temporarily Hidden) */}
                  {/*
                  <div className="space-y-3.5">
                    <h4 className="form-section-title text-base font-bold text-slate-800 uppercase tracking-wider pb-1">
                      Simulation Mode
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>Simulation Tenure (Years)</label>
                        <input
                          value={simulationTenure}
                          onChange={(e) => setSimulationTenure(e.target.value)}
                          className="select-none text-slate-800 bg-white"
                          required
                        />
                      </div>

                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>Simulation Type</label>
                        <Select
                          value={simulationType}
                          onChange={(val) => setSimulationType(val)}
                          className="bess-antd-select w-full"
                          options={[
                            { value: "1", label: "Solar + BESS" },
                            { value: "2", label: "Solar + Wind + BESS" }
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                  */}

                  {/* Losses & Efficiencies */}
                  <div className="mt-12 space-y-6">
                    <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                      System Losses Settings
                    </h4>

                    {/* Group 1: Stage 1 Line Parameters & Losses */}
                    {has220Line && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Stage 1 Conductor Resistance */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <span className={`text-xs font-semibold select-none ${useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"}`}>
                              {projectValues.tl_volt_stage_1 || 220}kV Conductor Resistance (Ohm/km)
                            </span>
                          </div>
                          <input
                            type="number" step="0.00001"
                            disabled={useDirectLineLoss220}
                            value={conductorResistance220}
                            onChange={(e) => setConductorResistance220(e.target.value)}
                            placeholder="0.066"
                            className={useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>

                        {/* Stage 1 Transmission Line Distance */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <span className={`text-xs font-semibold select-none ${useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"}`}>
                              {projectValues.tl_volt_stage_1 || 220}kV Transmission Line Distance (km)
                            </span>
                          </div>
                          <input
                            type="number" step="0.001"
                            disabled={useDirectLineLoss220}
                            value={lineDistance220}
                            onChange={(e) => setLineDistance220(e.target.value)}
                            placeholder="29.0"
                            className={useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>

                        {/* Stage 1 Transmission Line Loss (%) */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <input
                              type="checkbox"
                              checked={useDirectLineLoss220}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setUseDirectLineLoss220(checked);
                                if (!checked) setTransLineLoss220kv("");
                              }}
                              className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                            />
                            <span
                              onClick={() => {
                                const checked = !useDirectLineLoss220;
                                setUseDirectLineLoss220(checked);
                                if (!checked) setTransLineLoss220kv("");
                              }}
                              className={`text-xs font-semibold cursor-pointer select-none truncate ${!useDirectLineLoss220 ? "text-slate-400" : "text-[#0c2340]"}`}
                            >
                              {projectValues.tl_volt_stage_1 || 220}kV Transmission line loss (%)
                            </span>
                          </div>
                          <input
                            type="number" step="0.01"
                            disabled={!useDirectLineLoss220}
                            required={useDirectLineLoss220}
                            value={transLineLoss220kv}
                            onChange={(e) => setTransLineLoss220kv(e.target.value)}
                            placeholder="0.50"
                            className={!useDirectLineLoss220 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>
                      </div>
                    )}

                    {/* Group 2: Stage 2 Line Parameters & Losses */}
                    {has400Line && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                        {/* Stage 2 Conductor Resistance */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <span className={`text-xs font-semibold select-none ${useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"}`}>
                              {projectValues.tl_volt_stage_2 || 400}kV Conductor Resistance (Ohm/km)
                            </span>
                          </div>
                          <input
                            type="number" step="0.00001"
                            disabled={useDirectLineLoss400}
                            value={conductorResistance400}
                            onChange={(e) => setConductorResistance400(e.target.value)}
                            placeholder="0.066"
                            className={useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>

                        {/* Stage 2 Transmission Line Distance */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <span className={`text-xs font-semibold select-none ${useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"}`}>
                              {projectValues.tl_volt_stage_2 || 400}kV Transmission Line Distance (km)
                            </span>
                          </div>
                          <input
                            type="number" step="0.001"
                            disabled={useDirectLineLoss400}
                            value={lineDistance400}
                            onChange={(e) => setLineDistance400(e.target.value)}
                            placeholder="29.0"
                            className={useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>

                        {/* Stage 2 Transmission Line Loss (%) */}
                        <div className="flex flex-col gap-1 text-slate-700">
                          <div className="flex items-center gap-1.5 h-4 mb-1">
                            <input
                              type="checkbox"
                              checked={useDirectLineLoss400}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setUseDirectLineLoss400(checked);
                                if (!checked) setTransLineLoss400kv("");
                              }}
                              className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                            />
                            <span
                              onClick={() => {
                                const checked = !useDirectLineLoss400;
                                setUseDirectLineLoss400(checked);
                                if (!checked) setTransLineLoss400kv("");
                              }}
                              className={`text-xs font-semibold cursor-pointer select-none truncate ${!useDirectLineLoss400 ? "text-slate-400" : "text-[#0c2340]"}`}
                            >
                              {projectValues.tl_volt_stage_2 || 400}kV Transmission line loss (%)
                            </span>
                          </div>
                          <input
                            type="number" step="0.01"
                            disabled={!useDirectLineLoss400}
                            required={useDirectLineLoss400}
                            value={transLineLoss400kv}
                            onChange={(e) => setTransLineLoss400kv(e.target.value)}
                            placeholder="0.50"
                            className={!useDirectLineLoss400 ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                          />
                        </div>
                      </div>
                    )}

                    {/* Group 3: Cable & Equipment Losses (Carried from BESS, Always Disabled in Hybrid) */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                      {/* HT AC Cable Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">HT AC Cable Loss (%)</span>
                        </div>
                        <input
                          type="number" step="0.0001" value={htAcCableLoss}
                          disabled readOnly
                          placeholder="0.10"
                          className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                        />
                      </div>

                      {/* LT AC Cable Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">LT AC Cable Loss (%)</span>
                        </div>
                        <input
                          type="number" step="0.0001" value={ltAcCableLoss}
                          disabled readOnly
                          placeholder="0.33"
                          className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                        />
                      </div>

                      {/* LT DC Cable Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">LT DC Cable Loss (%)</span>
                        </div>
                        <input
                          type="number" step="0.0001" value={ltDcCableLoss}
                          disabled readOnly
                          placeholder="0.10"
                          className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                        />
                      </div>

                      {/* PCS Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">PCS Loss (%)</span>
                        </div>
                        <input
                          type="number" step="0.0001" value={pcsLoss}
                          disabled readOnly
                          placeholder="1.50"
                          className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                        />
                      </div>

                      {/* IDT Loss (%) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">IDT Loss (%)</span>
                        </div>
                        <input
                          type="number" step="0.0001" value={idtLoss}
                          disabled readOnly
                          placeholder="1.00"
                          className="bg-slate-100 text-slate-500 cursor-not-allowed border-slate-200"
                        />
                      </div>

                      {/* BESS IDT No-Load (MW) */}
                      <div className="flex flex-col gap-1 text-slate-700">
                        <div className="flex items-center justify-between h-4 mb-1">
                          <span className="text-xs font-semibold text-[#0c2340]">BESS IDT No-Load (MW)</span>
                        </div>
                        <input
                          type="number" step="0.0001" required value={bessIdtNoLoad}
                          onChange={(e) => setBessIdtNoLoad(e.target.value)}
                          placeholder="0.0375"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Wind IDT Transformer */}
                  {(simulationType === "2" || simulationType === 2) && (
                    <div className="mt-12 space-y-5">
                      <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                        Wind IDT (MVA)
                      </h4>
                      <div className="grid grid-cols-1 gap-6">
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1 text-slate-700">
                              <label className="text-[10px] font-bold text-slate-500 uppercase">Capacity (MVA)</label>
                              <input
                                type="number" step="0.0001" required={simulationType === "2" || simulationType === 2} value={windIdtCapacity}
                                onChange={(e) => setWindIdtCapacity(e.target.value)}
                                placeholder="50.0"
                              />
                            </div>
                            <div className="flex flex-col gap-1 text-slate-700">
                              <label className="text-[10px] font-bold text-slate-500 uppercase">No-Load (MW)</label>
                              <input
                                type="number" step="0.0001" required={simulationType === "2" || simulationType === 2} value={windIdtNoLoad}
                                onChange={(e) => setWindIdtNoLoad(e.target.value)}
                                placeholder="0.05"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grid Transformers */}
                  {(has220Transformer || has400Transformer) && (
                    <div className="mt-12 space-y-5">
                      <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                        Grid Transformers (MVA)
                      </h4>
                      <div className={`grid grid-cols-1 ${has220Transformer && has400Transformer ? "md:grid-cols-2" : ""} gap-6`}>
                        {has220Transformer && (
                          <div className="space-y-2">
                            <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{projectValues.tl_volt_stage_1 || 220}kV Transformer</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"}`}>Capacity (MWh)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans220Loss}
                                  disabled={useDirectTrans220Loss}
                                  value={trans220Capacity}
                                  onChange={(e) => setTrans220Capacity(e.target.value)}
                                  placeholder="37.5"
                                  className={useDirectTrans220Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"}`}>No-Load (MW)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans220Loss}
                                  disabled={useDirectTrans220Loss}
                                  value={trans220NoLoad}
                                  onChange={(e) => setTrans220NoLoad(e.target.value)}
                                  placeholder="0.0375"
                                  className={useDirectTrans220Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"}`}>Load Loss (MW)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans220Loss}
                                  disabled={useDirectTrans220Loss}
                                  value={trans220Load}
                                  onChange={(e) => setTrans220Load(e.target.value)}
                                  placeholder="0.3375"
                                  className={useDirectTrans220Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <input
                                    type="checkbox"
                                    checked={useDirectTrans220Loss}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setUseDirectTrans220Loss(checked);
                                      if (!checked) setTransLoss220kv("");
                                    }}
                                    className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                                  />
                                  <span
                                    onClick={() => {
                                      const checked = !useDirectTrans220Loss;
                                      setUseDirectTrans220Loss(checked);
                                      if (!checked) setTransLoss220kv("");
                                    }}
                                    className={`text-[10px] font-bold uppercase cursor-pointer select-none truncate ${!useDirectTrans220Loss ? "text-slate-400" : "text-[#0c2340]"}`}
                                  >
                                    Loss (%)
                                  </span>
                                </div>
                                <input
                                  type="number" step="0.01"
                                  disabled={!useDirectTrans220Loss}
                                  required={useDirectTrans220Loss}
                                  value={transLoss220kv}
                                  onChange={(e) => setTransLoss220kv(e.target.value)}
                                  placeholder="0.50"
                                  className={!useDirectTrans220Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {has400Transformer && (
                          <div className="space-y-2">
                            <h5 className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">{projectValues.tl_volt_stage_2 || 400}kV Transformer</h5>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"}`}>Capacity (MWh)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans400Loss}
                                  disabled={useDirectTrans400Loss}
                                  value={trans400Capacity}
                                  onChange={(e) => setTrans400Capacity(e.target.value)}
                                  placeholder="120.0"
                                  className={useDirectTrans400Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"}`}>No-Load (MW)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans400Loss}
                                  disabled={useDirectTrans400Loss}
                                  value={trans400NoLoad}
                                  onChange={(e) => setTrans400NoLoad(e.target.value)}
                                  placeholder="0.12"
                                  className={useDirectTrans400Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <span className={`text-[10px] font-bold uppercase truncate select-none ${useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"}`}>Load Loss (MW)</span>
                                </div>
                                <input
                                  type="number" step="0.0001"
                                  required={!useDirectTrans400Loss}
                                  disabled={useDirectTrans400Loss}
                                  value={trans400Load}
                                  onChange={(e) => setTrans400Load(e.target.value)}
                                  placeholder="1.08"
                                  className={useDirectTrans400Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                              <div className="flex flex-col gap-1 text-slate-700">
                                <div className="flex items-center gap-1.5 h-4 mb-1">
                                  <input
                                    type="checkbox"
                                    checked={useDirectTrans400Loss}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setUseDirectTrans400Loss(checked);
                                      if (!checked) setTransLoss400kv("");
                                    }}
                                    className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer rounded shrink-0"
                                  />
                                  <span
                                    onClick={() => {
                                      const checked = !useDirectTrans400Loss;
                                      setUseDirectTrans400Loss(checked);
                                      if (!checked) setTransLoss400kv("");
                                    }}
                                    className={`text-[10px] font-bold uppercase cursor-pointer select-none truncate ${!useDirectTrans400Loss ? "text-slate-400" : "text-[#0c2340]"}`}
                                  >
                                    Loss (%)
                                  </span>
                                </div>
                                <input
                                  type="number" step="0.01"
                                  disabled={!useDirectTrans400Loss}
                                  required={useDirectTrans400Loss}
                                  value={transLoss400kv}
                                  onChange={(e) => setTransLoss400kv(e.target.value)}
                                  placeholder="0.50"
                                  className={!useDirectTrans400Loss ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : ""}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Unavailability & Evacuation Capacity */}
                  <div className="mt-12 space-y-5">
                    <h4 className="form-section-title text-lg font-bold text-slate-800 uppercase tracking-wider pb-1">
                      Unavailability & Evacuation Settings
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>Solar availability at 33kV (%)</label>
                        <input
                          type="number" step="0.001" required value={dt33kvPct}
                          onChange={(e) => setDt33kvPct(e.target.value)}
                          placeholder="99.40"
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>BESS Availability (%)</label>
                        <input
                          type="number" step="0.0001" required value={bessUnavailability}
                          onChange={(e) => setBessUnavailability(e.target.value)}
                          placeholder="1.50"
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>Grid availability</label>
                        <input
                          type="number" step="0.0001" required value={gridUnavailability}
                          onChange={(e) => setGridUnavailability(e.target.value)}
                          placeholder="0.02"
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>Evacuation Capacity</label>
                        <input
                          type="number" step="0.001" required value={evacuationCapacity}
                          onChange={(e) => setEvacuationCapacity(e.target.value)}
                          placeholder="100.0"
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-slate-700">
                        <label>BESS Feeder Cap (MVA)</label>
                        <input
                          type="number" step="0.001" required value={bessFeederCapacity}
                          onChange={(e) => setBessFeederCapacity(e.target.value)}
                          placeholder="100.0"
                        />
                      </div>

                      {(simulationType === "1" || simulationType === 1 || simulationType === "2" || simulationType === 2) && (
                        <div className="flex flex-col gap-1 text-slate-700">
                          <label>Min BESS Stored Energy (%)</label>
                          <input
                            type="number" step="0.01" value={minBessStoreEnergy}
                            onChange={(e) => setMinBessStoreEnergy(e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                      )}
                      {(simulationType === "2" || simulationType === 2) && (
                        <>
                          <div className="flex flex-col gap-1 text-slate-700">
                            <label>Wind Total Losses (%) <span className="text-red-500">*</span></label>
                            <input
                              type="number" step="0.01" value={windTotalLossesPct}
                              onChange={(e) => setWindTotalLossesPct(e.target.value)}
                              placeholder="0.00"
                              required
                            />
                          </div>
                          <div className="flex flex-col gap-1 text-slate-700">
                            <label>Min Dispatch Power (%)</label>
                            <input
                              type="number" step="0.01" value={minDispatchPower}
                              onChange={(e) => setMinDispatchPower(e.target.value)}
                              placeholder="0.00"
                            />
                          </div>
                          <div className="flex flex-col gap-1 text-slate-700">
                            <label>Battery Degradation Cycle Count At</label>
                            <Select
                              value={bessDegradation ?? 1}
                              onChange={(val) => setBessDegradation(Number(val))}
                              className="bess-antd-select w-full"
                              style={{ height: 38 }}
                              options={[
                                { value: 1, label: "At Actual" },
                                { value: 2, label: "At Factor" },
                              ]}
                            />
                          </div>
                          {Number(bessDegradation ?? 1) === 1 && (
                            <div className="flex flex-col gap-1 text-slate-700">
                              <label>Adjusting Factor</label>
                              <input
                                type="number"
                                step="0.0001"
                                min="1"
                                value={adjustingFactor}
                                onChange={(e) => setAdjustingFactor(e.target.value)}
                                placeholder="1.0000"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Submit Actions */}



                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="bess-btn-primary"
                    >
                      {submitting ? (
                        <>
                          <LoadingOutlined className="animate-spin" /> Saving...
                        </>
                      ) : (
                        <>
                          Save & Continue →
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {currentStep === 3 && (
                <div className="space-y-6 animate-fade-in">
                  {/* Bulk Fill Bar */}
                  <div className="bg-[#f8fafc] border border-slate-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-xs font-bold text-slate-700">Set Default % for All Years:</span>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="e.g. 95"
                        value={bulkMinBessPct}
                        onChange={(e) => setBulkMinBessPct(e.target.value)}
                        className="h-8 w-24 px-2.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-800 bg-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (bulkMinBessPct === "") return;
                          const val = Number(bulkMinBessPct);
                          setYearlyMinBessStoreEnergy((prev) =>
                            prev.map((item) => ({ ...item, pct: val }))
                          );
                          setMinBessStoreEnergy(bulkMinBessPct);
                          message.success(`Set Min BESS Stored Energy to ${val}% for all years.`);
                        }}
                        className="h-8 px-3 bg-[#0052CC] text-white hover:bg-[#0043a4] rounded-lg text-xs font-bold transition-all cursor-pointer shadow-sm"
                      >
                        Apply to All Years
                      </button>
                    </div>
                    <div className="text-xs text-slate-400 font-medium">
                      Simulation Tenure: <span className="font-bold text-slate-700">{getTenureCount()} Years</span>
                    </div>
                  </div>

                  {/* Yearly Table */}
                  <div className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm" style={{ maxHeight: '500px' }}>
                    <table className="bess-table">
                      <thead>
                        <tr>
                          <th className="w-36">Project Year</th>
                          <th>Min Base Stored Energy Target (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearlyMinBessStoreEnergy.map((item, idx) => (
                          <tr key={item.year} className="hover:bg-slate-50/50">
                            <td className="font-bold text-slate-700">Year {item.year}</td>
                            <td>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.pct ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value === "" ? "" : Number(e.target.value);
                                    setYearlyMinBessStoreEnergy((prev) =>
                                      prev.map((r, i) => (i === idx ? { ...r, pct: val } : r))
                                    );
                                  }}
                                  className="h-8 w-32 px-2.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-blue-500 bg-white"
                                />
                                <span className="text-xs font-semibold text-slate-400">%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Stepper Navigation */}
                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleStep3YearlySubmit}
                      disabled={savingYearlyMinBess}
                      className="bess-btn-primary"
                    >
                      {savingYearlyMinBess ? <LoadingOutlined /> : "Save & Continue →"}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-6">
                  {loadingDischarge ? (
                    <div className="text-center py-20 text-slate-400 text-xs">
                      <LoadingOutlined className="text-xl mb-2 text-[#0052CC] animate-spin" />
                      <div>Loading discharge profile configurations...</div>
                    </div>
                  ) : (
                    <div className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm" style={{ maxHeight: '600px' }}>
                      <table className="bess-table">
                        <thead>
                          <tr>
                            <th className="text-center w-16">
                              <div className="flex flex-col items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={isAllMatrixChecked}
                                  onChange={(e) => handleToggleAllMatrix(e.target.checked)}
                                />
                                <span className="text-[10px] uppercase font-bold text-slate-500">Toggle</span>
                              </div>
                            </th>
                            <th>Hour</th>
                            {MONTHS.map((m) => (
                              <th key={m.key} className="text-center w-16">
                                <div className="flex flex-col items-center gap-1.5">
                                  <input
                                    type="checkbox"
                                    className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                    checked={dischargeProfile.length > 0 && dischargeProfile.every((row) => row[m.key] === true)}
                                    onChange={(e) => handleToggleColumn(m.key, e.target.checked)}
                                  />
                                  <span className="text-[10px] uppercase font-bold text-slate-500">{m.label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dischargeProfile.map((row) => (
                            <tr key={row.hour} className="hover:bg-slate-50/50">
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={MONTHS.every((m) => row[m.key] === true)}
                                  onChange={(e) => handleToggleRow(row.hour, e.target.checked)}
                                />
                              </td>
                              <td className="font-bold">
                                Hour {row.hour.toString().padStart(2, "0")}:00
                              </td>
                              {MONTHS.map((m) => (
                                <td key={m.key} className="text-center">
                                  <input
                                    type="checkbox"
                                    checked={row[m.key] || false}
                                    onChange={() => handleToggleCell(row.hour, m.key)}
                                    className="w-4 h-4 accent-[#0052CC] rounded cursor-pointer"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleStep4DischargeSubmit}
                      disabled={savingDischarge}
                      className="bess-btn-primary"
                    >
                      {savingDischarge ? <LoadingOutlined /> : "Save & Continue →"}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 5 && (
                <div className="space-y-6">
                  {loadingCharging ? (
                    <div className="text-center py-20 text-slate-400 text-xs">
                      <LoadingOutlined className="text-xl mb-2 text-[#0052CC] animate-spin" />
                      <div>Loading charging profile configurations...</div>
                    </div>
                  ) : (
                    <div className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm" style={{ maxHeight: '600px' }}>
                      <table className="bess-table">
                        <thead>
                          <tr>
                            <th className="text-center w-16">
                              <div className="flex flex-col items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={isAllChargingMatrixChecked}
                                  onChange={(e) => handleToggleChargingAllMatrix(e.target.checked)}
                                />
                                <span className="text-[10px] uppercase font-bold text-slate-500">Toggle</span>
                              </div>
                            </th>
                            <th>Hour</th>
                            {MONTHS.map((m) => (
                              <th key={m.key} className="text-center w-16">
                                <div className="flex flex-col items-center gap-1.5">
                                  <input
                                    type="checkbox"
                                    className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                    checked={chargingProfile.length > 0 && chargingProfile.every((row) => row[m.key] === true)}
                                    onChange={(e) => handleToggleChargingColumn(m.key, e.target.checked)}
                                  />
                                  <span className="text-[10px] uppercase font-bold text-slate-500">{m.label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chargingProfile.map((row) => (
                            <tr key={row.hour} className="hover:bg-slate-50/50">
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={MONTHS.every((m) => row[m.key] === true)}
                                  onChange={(e) => handleToggleChargingRow(row.hour, e.target.checked)}
                                />
                              </td>
                              <td className="font-bold">
                                Hour {row.hour.toString().padStart(2, "0")}:00
                              </td>
                              {MONTHS.map((m) => (
                                <td key={m.key} className="text-center">
                                  <input
                                    type="checkbox"
                                    checked={row[m.key] || false}
                                    onChange={() => handleToggleChargingCell(row.hour, m.key)}
                                    className="w-4 h-4 accent-[#0052CC] rounded cursor-pointer"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleStep5ChargingSubmit}
                      disabled={savingCharging}
                      className="bess-btn-primary"
                    >
                      {savingCharging ? <LoadingOutlined /> : "Save & Continue →"}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 6 && (
                <div className="space-y-6">
                  {loadingGridCharging ? (
                    <div className="text-center py-20 text-slate-400 text-xs">
                      <LoadingOutlined className="text-xl mb-2 text-[#0052CC] animate-spin" />
                      <div>Loading grid charging profile configurations...</div>
                    </div>
                  ) : (
                    <div className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm" style={{ maxHeight: '600px' }}>
                      <table className="bess-table">
                        <thead>
                          <tr>
                            <th className="text-center w-16">
                              <div className="flex flex-col items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={isAllGridChargingMatrixChecked}
                                  onChange={(e) => handleToggleGridChargingAllMatrix(e.target.checked)}
                                />
                                <span className="text-[10px] uppercase font-bold text-slate-500">Toggle</span>
                              </div>
                            </th>
                            <th>Hour</th>
                            {MONTHS.map((m) => (
                              <th key={m.key} className="text-center w-16">
                                <div className="flex flex-col items-center gap-1.5">
                                  <input
                                    type="checkbox"
                                    className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                    checked={gridChargingProfile.length > 0 && gridChargingProfile.every((row) => row[m.key] === true)}
                                    onChange={(e) => handleToggleGridChargingColumn(m.key, e.target.checked)}
                                  />
                                  <span className="text-[10px] uppercase font-bold text-slate-500">{m.label}</span>
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {gridChargingProfile.map((row) => (
                            <tr key={row.hour} className="hover:bg-slate-50/50">
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  className="w-3.5 h-3.5 accent-[#0052CC] cursor-pointer"
                                  checked={MONTHS.every((m) => row[m.key] === true)}
                                  onChange={(e) => handleToggleGridChargingRow(row.hour, e.target.checked)}
                                />
                              </td>
                              <td className="font-bold">
                                Hour {row.hour.toString().padStart(2, "0")}:00
                              </td>
                              {MONTHS.map((m) => (
                                <td key={m.key} className="text-center">
                                  <input
                                    type="checkbox"
                                    checked={row[m.key] || false}
                                    onChange={() => handleToggleGridChargingCell(row.hour, m.key)}
                                    className="w-4 h-4 accent-[#0052CC] rounded cursor-pointer"
                                  />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(5)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleStep6GridChargingSubmit}
                      disabled={savingGridCharging}
                      className="bess-btn-primary"
                    >
                      {savingGridCharging ? <LoadingOutlined /> : "Save & Continue →"}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 7 && (
                <div className="space-y-6">
                  <div className="space-y-4 max-w-xl">
                    {profileFilename && !uploadFile && (
                      <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-xl flex items-center justify-between text-xs text-blue-900 shadow-sm">
                        <div className="flex items-center gap-2.5">
                          <FileExcelOutlined className="text-blue-600 text-base" />
                          <div>
                            <div><strong>Previously uploaded profile:</strong> <code className="bg-white px-2 py-0.5 rounded border border-blue-200 text-blue-700 font-semibold">{profileFilename}</code></div>
                            <div className="text-[11px] text-blue-600/80 mt-0.5">Proceeding without uploading a new file will use this existing data.</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-3 text-slate-700">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Data Upload Type</label>
                      <div className="flex p-1 bg-slate-100 rounded-xl w-fit mb-2 border border-slate-200">
                        <button
                          type="button"
                          onClick={() => {
                            setUploadDataType("single");
                            if (!solarDegradation) setSolarDegradation("0.4");
                          }}
                          className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${uploadDataType === "single"
                              ? "bg-white text-[#0052cc] shadow-sm border border-slate-200/60"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent"
                            }`}
                        >
                          Single-Year Data
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setUploadDataType("full");
                            setSolarDegradation(""); // clear when disabled
                          }}
                          className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all ${uploadDataType === "full"
                              ? "bg-white text-[#0052cc] shadow-sm border border-slate-200/60"
                              : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50 border border-transparent"
                            }`}
                        >
                          Full Project Tenure Data
                        </button>
                      </div>
                    </div>

                    {uploadDataType === "single" && (
                      <div className="flex flex-col gap-1 text-slate-700 mt-2">
                        <label>Solar Degradation Rate (% per Year) <span className="text-red-500">*</span></label>
                        <InputNumber
                          min={0}
                          max={100}
                          step={0.01}
                          controls={false}
                          value={solarDegradation}
                          onChange={(val) => setSolarDegradation(val?.toString() || "")}
                          className="w-full h-10 rounded-xl text-sm border-slate-200"
                          placeholder="e.g. 0.5"
                        />
                        <span className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">This rate will be used to automatically simulate subsequent years based on the single year uploaded.</span>
                      </div>
                    )}

                    <div className="flex flex-col gap-1 text-slate-700 pt-2">
                      <label>Select Generation Excel File (.xlsx)</label>
                      <div className={`border-2 border-dashed rounded-2xl p-8 text-center bg-slate-50/50 hover:border-slate-300 transition-colors flex flex-col items-center justify-center ${uploadError ? 'border-red-500 bg-red-50/10' : 'border-slate-200'
                        }`}>
                        <UploadOutlined className="text-slate-400 text-3xl mb-3" />
                        <p className="text-xs text-slate-500 mb-4 leading-relaxed max-w-sm">
                          Drag and drop your generation profile Excel spreadsheet here, or select browse files. Make sure the template format matches the selected data type above.
                        </p>
                        <div className="flex gap-3 justify-center mb-1">
                          <input
                            type="file"
                            id="hybrid-file-upload"
                            className="hidden"
                            accept=".xlsx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setUploadFile(file);
                              if (file) {
                                setUploadError(false);
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById("hybrid-file-upload").click()}
                            className="h-9 px-4 bg-white border border-slate-200 text-slate-700 hover:text-slate-400 rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            Browse Files
                          </button>
                          <button
                            type="button"
                            onClick={handleDownloadTemplate}
                            className="h-9 px-4 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-100 rounded-lg text-xs font-semibold shadow-sm transition-all cursor-pointer flex items-center gap-1"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            <FileExcelOutlined /> Download Template
                          </button>
                        </div>
                        {uploadFile && (
                          <div className="mt-4 text-xs text-emerald-600 font-semibold animate-fade-in">
                            Selected: <span className="text-[#0052CC]">{uploadFile.name}</span> ({(uploadFile.size / 1024).toFixed(1)} KB)
                          </div>
                        )}
                        {uploadError && (
                          <div className="text-red-500 text-xs mt-2 font-semibold">
                            Please upload a valid generation profile spreadsheet before continuing.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(6)}
                      className="bess-btn-secondary"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={handleStep7UploadSubmit}
                      disabled={uploadingProfile}
                      className="bess-btn-primary"
                    >
                      {uploadingProfile ? (
                        <LoadingOutlined />
                      ) : (loadedFromHistory || profileFilename) && !uploadFile ? (
                        "Use Existing Profile & Continue →"
                      ) : (
                        "Upload & Continue →"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {currentStep === 8 && (
                <div className="space-y-6">
                  <div className="border border-slate-100 bg-slate-50/50 rounded-2xl p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <h4 className="font-bold text-slate-800 text-base mb-1">
                        Execute BESS Hybrid Simulation
                      </h4>
                      {/* <p className="text-xs text-slate-500">
                        Trigger the T-SQL simulation for configuration #{createdSimId} and dynamic generation data.
                      </p> */}
                    </div>
                    <button
                      type="button"
                      onClick={handleRunSimulation}
                      disabled={runningSim}
                      className="bess-btn-primary bg-emerald-600 hover:bg-emerald-700"
                      style={{ backgroundColor: '#10b981 !important', height: '48px', padding: '0 28px' }}
                    >
                      {runningSim ? <LoadingOutlined className="animate-spin" /> : <PlayCircleOutlined />} {loadedFromHistory ? "Re-run Simulation" : "Run Simulation"}
                    </button>
                  </div>

                  {simulationResults && (
                    <div className="space-y-8 animate-fade-in pt-4">
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                          <h3 className="font-bold text-slate-800 text-base uppercase tracking-wide">Yearly Summary Calculations</h3>
                          {simulationResults?.yearly_summary && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-slate-500">Report Year:</span>
                              <Select
                                value={selectedExportYear}
                                onChange={(val) => setSelectedExportYear(val)}
                                style={{ width: 120 }}
                                size="small"
                                className="text-xs"
                                options={[
                                  { value: "all", label: "All Years" },
                                  ...simulationResults.yearly_summary.map((r) => ({
                                    value: r.year,
                                    label: `Year ${r.year}`,
                                  })),
                                ]}
                              />
                              <Button
                                type="primary"
                                icon={downloadingExcelYear === selectedExportYear ? <LoadingOutlined /> : <FileExcelOutlined />}
                                onClick={() => handleDownloadYearExcel(selectedExportYear)}
                                loading={downloadingExcelYear === selectedExportYear}
                                style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                                className="flex items-center text-xs font-semibold"
                              >
                                Download Excel {selectedExportYear === "all" ? "(All Years)" : `(Year ${selectedExportYear})`}
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="overflow-auto border border-slate-100 rounded-xl bg-white shadow-sm" style={{ maxHeight: '600px' }}>
                          <table className="bess-table">
                            <thead>
                              <tr>
                                <th>Yr</th>
                                <th>Solar Gen (MWh)</th>
                                <th>Solar Exported (MWh)</th>
                                <th>Solar to BESS (MWh)</th>
                                <th>Grid Import (MWh)</th>
                                <th>BESS POI (MWh)</th>
                                <th>BESS Cap (MWh)</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {simulationResults.yearly_summary.map((record) => (
                                <tr key={record.year} className="hover:bg-slate-50/30">
                                  <td className="font-bold">Yr {record.year}</td>
                                  <td>{record.total_solar_gen.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>{record.total_export_solar_power.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>{record.total_solar_to_bess_power.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>{record.total_grid_import.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>{record.total_bess_discharge.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>{record.bess_capacity.toLocaleString(undefined, { maximumFractionDigits: 2 })} MWh</td>
                                  <td>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={() => handleLoadHourlyDetails(record.year)}
                                        className="px-3 py-1.5 bg-[#f0f9ff] text-[#0052CC] hover:bg-[#e0f2fe] border border-sky-100 rounded-lg font-semibold text-xs cursor-pointer transition-all shadow-sm"
                                      >
                                        Detailed Daily Profile
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDownloadYearExcel(record.year)}
                                        disabled={downloadingExcelYear === record.year}
                                        className="px-3 py-1.5 bg-[#f0fdf4] text-[#16a34a] hover:bg-[#dcfce7] border border-emerald-100 rounded-lg font-semibold text-xs cursor-pointer transition-all shadow-sm flex items-center gap-1"
                                        title={`Download Excel report for Year ${record.year}`}
                                      >
                                        {downloadingExcelYear === record.year ? <LoadingOutlined /> : <FileExcelOutlined />}
                                        <span>Excel (Yr {record.year})</span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {selectedDetailsYear && (
                        <div className="border border-slate-100 rounded-2xl p-6 bg-white shadow-sm space-y-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                              <h4 className="font-bold text-slate-800 text-sm">
                                Year {selectedDetailsYear} Hourly Generation & Dispatch Plots
                              </h4>
                              <p className="text-xs text-slate-400 mt-0.5">
                                Select a day index (1-365) to render 24 hourly simulation values.
                              </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              <div className="min-w-[200px] max-w-[320px]">
                                <Select
                                  mode="multiple"
                                  allowClear
                                  placeholder="Select fields to graph"
                                  value={selectedTrendFields}
                                  onChange={(vals) => setSelectedTrendFields(vals)}
                                  options={TREND_FIELD_CONFIG.map(f => ({ label: f.label, value: f.key }))}
                                  style={{ width: "100%" }}
                                  maxTagCount="responsive"
                                  size="small"
                                />
                              </div>
                              <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                <span className="text-xs font-bold text-slate-500 uppercase">Day index:</span>
                                <InputNumber
                                  min={1}
                                  max={365}
                                  value={selectedChartDay}
                                  onChange={(val) => setSelectedChartDay(val || 1)}
                                  className="w-16 h-8 text-xs rounded border-slate-200"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="w-full">
                            <Slider
                              min={1}
                              max={365}
                              value={selectedChartDay}
                              onChange={setSelectedChartDay}
                              tooltip={{ formatter: (v) => `Day ${v}` }}
                            />
                          </div>

                          {loadingHourlyData ? (
                            <div className="text-center py-20 text-slate-400 text-xs">
                              <LoadingOutlined className="animate-spin text-lg text-[#0052CC] mb-2" />
                              <div>Loading hourly generation curves...</div>
                            </div>
                          ) : activeDayData.length > 0 ? (
                            <div className="h-80 w-full">
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={activeDayData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                  <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} tickFormatter={(h) => `${h}:00`} />
                                  <YAxis stroke="#94a3b8" fontSize={11} label={{ value: "Power (MW)", angle: -90, position: "insideLeft", fontSize: 11 }} />
                                  <Tooltip
                                    contentStyle={{ borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)" }}
                                    labelFormatter={(h) => `Hour ${h}:00`}
                                    formatter={(v, name) => [parseFloat(v).toFixed(2), name]}
                                  />
                                  <Legend verticalAlign="top" height={36} wrapperStyle={{ fontSize: 11 }} />
                                  {TREND_FIELD_CONFIG.filter(f => selectedTrendFields.includes(f.key)).map(f => (
                                    <Line
                                      key={f.key}
                                      type="monotone"
                                      dataKey={f.key}
                                      name={f.label}
                                      stroke={f.color}
                                      strokeWidth={2}
                                      dot={false}
                                    />
                                  ))}
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="text-center py-20 text-slate-400 text-xs italic">
                              No time-block data found for Day {selectedChartDay}.
                            </div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-end pt-6 border-t border-slate-100">
                        <button
                          onClick={() => {
                            setCreatedSimId(null);
                            setCurrentStep(1);
                            setSimulationResults(null);
                            setUploadFile(null);
                            setUploadError(false);
                            setSelectedDetailsYear(null);
                            setHourlyProfileData([]);
                            setLoadedFromHistory(false);
                          }}
                          className="bess-btn-primary"
                        >
                          Create Another Simulation
                        </button>
                      </div>
                    </div>
                  )}

                  {!simulationResults && (
                    <div className="flex justify-between items-center pt-6 border-t border-slate-100 mt-8">
                      <button
                        type="button"
                        onClick={() => setCurrentStep(6)}
                        className="bess-btn-secondary"
                      >
                        Back
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {loadingList ? (
              <div className="flex-1 flex flex-col justify-center items-center py-12 text-center text-slate-400 text-xs bess-card">
                <LoadingOutlined className="text-xl mb-2 text-[#0052CC] animate-spin" />
                <div>Loading hybrid simulation runs...</div>
              </div>
            ) : (
              <>
                {/* Search bar row */}
                <div className="w-full max-w-xs relative mb-6">
                  <input
                    type="text"
                    placeholder="Search simulations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 w-full pl-4 pr-10 border border-slate-200 rounded-lg text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 shadow-sm bg-white"
                  />
                  <SearchOutlined className="absolute right-3.5 top-3.5 text-slate-400 text-[13px]" />
                </div>

                {(() => {
                  const filteredSimulations = hybridSimulations
                    .filter((sim) => {
                      const query = searchQuery.trim().toLowerCase();
                      if (!query) return true;
                      const idStr = sim.id.toString();
                      const hashIdStr = `#${sim.id}`;
                      const sizingSimIdStr = sim.bess_sizing_simulation?.toString() || "";
                      const sizingSimNameStr = (sim.bess_sizing_simulation_name || "").toLowerCase();
                      return idStr.includes(query) || hashIdStr.includes(query) || sizingSimIdStr.includes(query) || sizingSimNameStr.includes(query);
                    })
                    .sort((a, b) => {
                      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
                      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
                      if (timeB !== timeA) return timeB - timeA;
                      return (Number(b.id) || 0) - (Number(a.id) || 0);
                    });

                  if (filteredSimulations.length > 0) {
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSimulations.map((sim) => {
                          const typeLabel = sim.simulation_type === 1 ? "Solar + BESS" : "Solar + Wind + BESS";
                          const dateObj = sim.created_at ? new Date(sim.created_at) : null;
                          const formattedDate = dateObj
                            ? `${dateObj.toLocaleDateString()} ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                            : "Draft (Not Run)";

                          const leftBorderColor = sim.simulation_type === 1 ? "border-l-[#2563eb]" : "border-l-[#16a34a]";
                          const badgeClass = sim.simulation_type === 1
                            ? "bg-blue-50 text-blue-600 border border-blue-100"
                            : "bg-emerald-50 text-emerald-600 border border-emerald-100";

                          const hybridName = getHybridSimName(sim);
                          return (
                            <div
                              key={sim.id}
                              className={`p-6 border border-slate-200 border-l-4 ${leftBorderColor} rounded-2xl bg-white shadow-sm`}
                            >
                              <div className="flex justify-between items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="font-bold text-sm text-slate-800 truncate" title={hybridName}>
                                    {hybridName}
                                  </div>
                                  <div className="text-[11px] text-slate-400 mt-1 font-medium">{formattedDate}</div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${badgeClass}`}>
                                    {typeLabel}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteHybridSimulation(sim.id);
                                    }}
                                    className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-slate-50 cursor-pointer border-none bg-transparent flex items-center justify-center"
                                    title="Delete Simulation"
                                  >
                                    <DeleteOutlined style={{ fontSize: '14px' }} />
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                                <div className="flex flex-col">
                                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Tenure</span>
                                  <span className="font-bold text-slate-800 text-[13px] mt-0.5">{sim.simulation_tenure} Yrs</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Evac Capacity</span>
                                  <span className="font-bold text-slate-800 text-[13px] mt-0.5">{parseFloat(sim.evacuation_capacity_mva).toFixed(1)} MVA</span>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Sizing Sim</span>
                                  <span className="font-bold text-slate-800 text-[13px] mt-0.5 truncate" title={sim.bess_sizing_simulation_name || `#${sim.bess_sizing_simulation}`}>
                                    #{sim.bess_sizing_simulation}
                                  </span>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-3 mt-[30px]">
                                <button
                                  type="button"
                                  onClick={() => handleLoadSimulation(sim)}
                                  className="w-full py-2 border border-blue-500 text-blue-600 bg-white hover:bg-blue-50/50 rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1"
                                >
                                  Load Config
                                </button>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/hybrid-bess/${sim.id}`)}
                                  className="w-full py-2 text-white bg-[#0052cc] hover:bg-[#0747a6] rounded-lg font-bold text-xs cursor-pointer transition-all shadow-sm flex items-center justify-center gap-1 border-none"
                                >
                                  View Results
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  } else {
                    return (
                      <div className="text-center py-12 bg-white border border-slate-100 rounded-2xl text-slate-400 text-sm italic bess-card">
                        {searchQuery.trim() ? "No simulations match your search query." : "No past sizing simulations found."}
                      </div>
                    );
                  }
                })()}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}